import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { t } from '../i18n';
import LangSwitch from '../components/LangSwitch';
import { useTheme } from '../contexts/ThemeContext';
import { productionAPI, warehouseAPI, gradeAPI, printSheetLabelBySize, printViaIframe,
         setPressWorker, setPressClient } from '../api';
import { confirmToast } from '../components/ConfirmToast';
import { setBusy } from '../utils/autoUpdate';
import toast from 'react-hot-toast';

// ── Бирка / принтер созламалари ───────────────────────────────────────────────
// Ҳар пресс терминали учун алоҳида сақланади (браузер localStorage'ида, яъни
// ўша компьютерда). Бир марта ёқиб қўйилса — ҳар «+1» да бирка ўзи чиқаверади,
// янги ойна ОЧИЛМАЙДИ (яширин iframe орқали тўғридан-тўғри принтерга кетади).
const PRINT_KEY     = 'lamtech_print_cfg_v2';
const AUTOPRINT_KEY = 'operator_autoprint';         // эски созлама — мослик учун
const DEFAULT_CFG   = { auto: true, copies: 1, size: 'small', printer: '' };

// ── Один пресс — один моноблок ────────────────────────────────────────────────
// Ключ живёт в localStorage, то есть привязан к БРАУЗЕРУ, а не к оператору:
// один и тот же человек может подойти к другому прессу, а вот два моноблока
// на одном прессе — нет, иначе оба жмут «+1» по одному заданию и счётчик врёт.
const CLIENT_KEY = 'lamtech_monoblock_id';
const clientId = (() => {
  let v = localStorage.getItem(CLIENT_KEY);
  if (!v) {
    v = 'mb-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem(CLIENT_KEY, v);
  }
  return v;
})();

// Терминал сразу называет себя: дальше это уедет с каждым действием у пресса,
// и в записи будет видно, что работу вели от станка, а не из кабинета.
setPressClient(clientId);

// Смена дневная и ночная: у пресса днём светло от окон, ночью — только лампы.
// Поэтому переключатель темы нужен прямо в терминале, а не только в кабинете.
function ThemeBtn() {
  const { isDark, toggle } = useTheme();
  return (
    <button onClick={toggle} className="touch-btn"
      title={isDark ? t('Светлая тема') : t('Тёмная тема')}
      style={{
        background: 'var(--bg)', border: '1px solid var(--border)',
        borderRadius: 8, color: 'var(--muted)', padding: '7px 12px',
        cursor: 'pointer', fontSize: 15, lineHeight: 1,
      }}>{isDark ? '☀' : '🌙'}</button>
  );
}

// У пресса стоит моноблок — клавиатуры там нет, а F11 нужен: в полном экране
// не видно ни адресной строки, ни вкладок, и оператор ничего не закроет
// случайным касанием. Кнопка делает ровно то же, что и клавиша.
function FullscreenBtn() {
  const [full, setFull] = useState(() => !!document.fullscreenElement);

  useEffect(() => {
    const onChange = () => setFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggle = () => {
    const el = document.documentElement;
    if (document.fullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen || (() => {})).call(document);
    } else {
      (el.requestFullscreen || el.webkitRequestFullscreen || (() => {})).call(el);
    }
  };

  return (
    <button onClick={toggle} className="touch-btn"
      title={full ? t('Обычный режим (F11)') : t('Во весь экран (F11)')}
      style={{
        background: 'var(--bg)', border: '1px solid var(--border)',
        borderRadius: 8, color: 'var(--muted)', padding: '7px 12px',
        cursor: 'pointer', fontSize: 15, lineHeight: 1,
      }}>{full ? '🗗' : '⛶'}</button>
  );
}

const readAll = () => {
  try { return JSON.parse(localStorage.getItem(PRINT_KEY) || '{}') || {}; }
  catch (e) { return {}; }
};
const loadPrintCfg = (pressId) => {
  const legacy = localStorage.getItem(AUTOPRINT_KEY);
  const base   = { ...DEFAULT_CFG, ...(legacy !== null ? { auto: legacy !== '0' } : {}) };
  return { ...base, ...(readAll()[String(pressId || 'default')] || {}) };
};
const savePrintCfg = (pressId, cfg) => {
  const all = readAll();
  all[String(pressId || 'default')] = cfg;
  localStorage.setItem(PRINT_KEY, JSON.stringify(all));
  localStorage.setItem(AUTOPRINT_KEY, cfg.auto ? '1' : '0');   // эски калитни ҳам синхрон тутамиз
};

// Бирка ўлчовлари — синов бирка ва созламалардаги тугмалар шу рўйхатдан олинади
const LABEL_SIZES = {
  mini:  { w: '30mm', h: '20mm', title: '7pt',  sub: '4.5pt', name: '30×20 mm' },
  small: { w: '40mm', h: '25mm', title: '9pt',  sub: '5.5pt', name: '40×25 mm' },
  big:   { w: '70mm', h: '50mm', title: '16pt', sub: '8pt',   name: '70×50 mm' },
};

// Синов бирка — принтер/ўлчов тўғри созланганини текшириш учун (серверсиз)
const printTestLabel = (cfg) => {
  const s = LABEL_SIZES[cfg.size] || LABEL_SIZES.small;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    @page { size: ${s.w} ${s.h}; margin: 0; }
    html,body { width:${s.w}; height:${s.h};
                margin:0; padding:2mm; box-sizing:border-box; overflow:hidden;
                font-family:Arial, sans-serif; display:flex; flex-direction:column;
                align-items:center; justify-content:center; gap:1mm; }
    .t { font-size:${s.title}; font-weight:900; }
    .s { font-size:${s.sub}; color:#555; text-align:center; }
  </style></head><body>
    <div class="t">ТЕСТ</div>
    <div class="s">LAMTECH MES · ${s.name}</div>
    <div class="s">${new Date().toLocaleString('ru-RU')}</div>
  </body></html>`;
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  return printViaIframe(url).then(() => setTimeout(() => URL.revokeObjectURL(url), 5000));
};

function Clock() {
  const [time, setTime] = useState(
    new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  );
  useEffect(() => {
    const t = setInterval(() => setTime(
      new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    ), 10000);
    return () => clearInterval(t);
  }, []);
  return (
    <span style={{
      background: 'var(--bg)', borderRadius: 8,
      padding: '6px 14px', color: '#f59e0b', fontSize: 18, fontWeight: 700
    }}>🕐 {time}</span>
  );
}

// ── Выбор пресса ──────────────────────────────────────────────────────────────
function SelectPressScreen({ onSelect }) {
  const [presses, setPresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const userName = sessionStorage.getItem('user_name') || 'Оператор';

  const STATUS = {
    working:   { color: '#22c55e', label: 'Работает' },
    idle:      { color: 'var(--dim)', label: 'Простой' },
    retool:    { color: '#f59e0b', label: 'Переналадка' },
    breakdown: { color: '#ef4444', label: 'Поломка' },
  };

  // Занятые прессы видно сразу: не надо тыкать и получать отказ
  const [busy, setBusy] = useState({});
  const [claiming, setClaiming] = useState(null);

  const load = useCallback(() => {
    Promise.all([productionAPI.getPresses(), productionAPI.getPressSessions(clientId)])
      .then(([p, s]) => { setPresses(p.data); setBusy(s.data || {}); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [load]);

  // Работу у пресса ведёт моноблок. Смена, начатая отсюда, оставляет его без
  // её номера: на свой start он получает «по этому заданию уже идёт смена» и
  // дальше не может отправить ни одного листа — они оседают у него на диске.
  // Так за 21–22.09.2026 потерялся 191 лист.
  //
  // Смотреть это не мешает: список прессов открыт, задания видно. Стоп ровно
  // там, где начинается счёт.
  const canWeb = sessionStorage.getItem('can_web_terminal') === '1';

  // Пресс занимаем ДО входа: два моноблока на одном прессе — это два
  // счётчика по одному заданию, разбираться потом не по чему.
  const pick = async (press) => {
    setClaiming(press.id);
    try {
      await productionAPI.claimPress(press.id, clientId);
      onSelect(press);
    } catch (err) {
      const d = err.response?.data || {};
      if (err.response?.status === 403 && d.web_terminal_denied) {
        toast.error(d.error);
      } else if (err.response?.status === 409) {
        toast.error(d.error || t('Пресс занят другим моноблоком'));
        load();
      } else {
        toast.error(t('Не удалось занять пресс'));
      }
    } finally { setClaiming(null); }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 24
    }}>
      <div style={{ marginBottom: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>🏭</div>
        <h1 style={{ color: 'var(--text)', fontSize: 28, fontWeight: 700, margin: 0 }}>
          Добро пожаловать, {userName}
        </h1>
        <p style={{ color: 'var(--dim)', fontSize: 18, marginTop: 8 }}>
          {canWeb ? t('Выберите ваш пресс') : t('Работу у пресса ведёт моноблок')}
        </p>
      </div>

      {!canWeb && (
        <div style={{
          maxWidth: 620, marginBottom: 32, padding: '18px 22px',
          background: 'var(--card)', border: '2px solid #f59e0b',
          borderRadius: 16, textAlign: 'center',
        }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>🖥️</div>
          <div style={{ color: '#f59e0b', fontSize: 18, fontWeight: 700 }}>
            {t('Отсюда пресс занять нельзя')}
          </div>
          <div style={{ color: 'var(--mid)', fontSize: 15, marginTop: 10, lineHeight: 1.5 }}>
            {t('Считать выпуск должен моноблок у пресса. Смена, начатая из '
               + 'браузера, оставляет его без номера смены, и листы перестают '
               + 'доходить до MES.')}
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 14, marginTop: 10 }}>
            {t('Моноблок не работает? Попросите администратора поставить вам '
               + 'галочку «Разрешено вести смену из браузера».')}
          </div>
        </div>
      )}

      {loading ? <p style={{ color: 'var(--mid)', fontSize: 18 }}>{t('Загрузка...')}</p> : (
        <div className="mob-press-wrap" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
          {presses.map(press => {
            const cfg  = STATUS[press.status] || STATUS.idle;
            const held = busy[press.id];
            // Занят другим моноблоком — кнопка неактивна. Свой же терминал
            // (после перезагрузки страницы) заходит обратно свободно.
            const held_ = held && !held.mine;
            // Без права вести смену из браузера пресс не выбрать вовсе — но
            // причина другая, и надпись на кнопке тоже: «занят» сказало бы
            // неправду и отправило бы человека искать, кто его занял.
            const locked = held_ || !canWeb;
            return (
              <button key={press.id} onClick={() => pick(press)}
                disabled={locked || claiming === press.id}
                className="touch-btn mob-press-btn"
                title={held_ ? t('Пресс открыт на другом моноблоке')
                     : !canWeb ? t('Работу у пресса ведёт моноблок') : undefined}
                style={{
                  background: 'var(--card)',
                  border: `3px solid ${locked ? 'var(--border)' : cfg.color + '50'}`,
                  borderRadius: 24, padding: '40px 56px',
                  cursor: locked ? 'not-allowed' : 'pointer',
                  textAlign: 'center', minWidth: 240,
                  opacity: locked ? 0.5 : 1,
              }}>
                <div style={{ fontSize: 56, marginBottom: 16 }}>{held_ ? '🔒' : '⚙️'}</div>
                <div style={{ color: 'var(--text)', fontSize: 26, fontWeight: 700 }}>{press.name}</div>
                {held_ ? (
                  <div style={{ color: '#f59e0b', fontSize: 14, fontWeight: 700, marginTop: 10 }}>
                    {t('ЗАНЯТ')}
                    {held.holder ? <div style={{ color: 'var(--dim)', fontWeight: 400, marginTop: 3 }}>{held.holder}</div> : null}
                  </div>
                ) : (
                  <div style={{
                    color: cfg.color, fontSize: 16, fontWeight: 600,
                    marginTop: 10, textTransform: 'uppercase', letterSpacing: 0.5
                  }}>{claiming === press.id ? '…' : cfg.label}</div>
                )}
                {press.current_texture_name && (
                  <div style={{ color: 'var(--dim)', fontSize: 14, marginTop: 8 }}>
                    {press.current_texture_name}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <button onClick={() => { sessionStorage.clear(); window.location.href = '/login'; }} style={{
        marginTop: 48, background: 'none', border: '1px solid var(--border)',
        borderRadius: 10, color: 'var(--mid)', padding: '12px 28px',
        cursor: 'pointer', fontSize: 15
      }}>{t('Выйти')}</button>
    </div>
  );
}

// Партия ДСП была полем даты: маска сама расставляла точки после 2-й и 4-й
// цифры и выбрасывала всё, что не цифра. Но на пачке стоит не дата — там
// номер партии, и в нём бывают буквы и свой разделитель. Оператор набирал
// то, что видит, а поле превращало это в «31.02.2026». Теперь пишем как
// есть: сверять партию с поставщиком всё равно по бумаге, а не по нашему
// представлению о том, как она должна выглядеть.

// ── Цифровая клавиатура для полей режима ────────────────────────────────────
//
// У пресса стоит моноблок, и вводят на нём в рукавицах. Системная экранная
// клавиатура открывается буквенной, занимает пол-экрана и закрывает то самое
// поле, которое заполняют, — а нужны там только цифры и четыре знака: «/»
// между верхом и низом плиты, точка и запятая в дробях, тире и точка с запятой
// в диапазонах.
//
// Поэтому клавиатура своя, прямо в окне: крупные клавиши, ничего лишнего, и
// поле остаётся на виду. Вставляем в позицию курсора, а не в конец: поправить
// одну цифру в «190/195» иначе значило бы набрать всё заново.
const PAD_KEYS = [
  ['7', '8', '9', '/'],
  ['4', '5', '6', '.'],
  ['1', '2', '3', ','],
  ['0', '-', ';', 'back'],
];

function NumPad({ onKey, onClose }) {
  const KEY = {
    height: 56, borderRadius: 10, border: '1px solid var(--border)',
    background: 'var(--card)', color: 'var(--text)', fontSize: 23, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit', touchAction: 'manipulation',
  };
  return (
    <div style={{
      background: 'var(--bg)', border: '1px solid var(--border)',
      borderRadius: 12, padding: 10,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {PAD_KEYS.flat().map(k => (
          <button key={k} type="button" className="touch-btn"
            /* Не даём полю потерять фокус: иначе после первой же клавиши
               курсор уходит и вставлять становится некуда */
            onMouseDown={e => e.preventDefault()}
            onClick={() => onKey(k)}
            style={{
              ...KEY,
              ...(k === 'back'
                ? { background: '#f59e0b18', color: '#f59e0b', fontSize: 20 }
                : null),
            }}>
            {k === 'back' ? '⌫' : k}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button type="button" className="touch-btn"
          onMouseDown={e => e.preventDefault()} onClick={() => onKey('clear')}
          style={{ ...KEY, flex: 1, fontSize: 15, color: 'var(--muted)' }}>
          {t('Очистить')}
        </button>
        <button type="button" className="touch-btn"
          onMouseDown={e => e.preventDefault()} onClick={onClose}
          style={{ ...KEY, flex: 1, fontSize: 15, background: 'var(--accent-weak)', color: 'var(--accent)' }}>
          {t('Убрать клавиатуру')}
        </button>
      </div>
    </div>
  );
}

// ── Пресс параметрлари модали (боshlash / davom ettirish) ────────────────────
//
// Раньше поля шли в столбик и окно прокручивалось: кнопку «Начать» оператор не
// видел, пока не долистает, а у пресса листают пальцем в перчатке. Теперь всё
// на одном экране — эталон вынесен в строку под заголовком, парные величины
// стоят рядом. Ничего не прокручивается, кнопка всегда на виду.
function PressParamsModalBase({ item, mode, press: machine, saving, onConfirm, onCancel }) {
  const ref = {
    matrix: item.ref_matrix_name,
    temp:   item.ref_press_temperature,
    hold:   item.ref_press_hold_seconds,
    press:  item.ref_press_pressure,
    thick:  item.ref_thickness,
  };
  const prev = item.active_job || {};

  // Верх и низ — РАЗНЫЕ плиты со своими нагревателями, и станок присылает их
  // двумя числами. Одним полем «190/195» их писали от руки, и разобрать потом
  // обратно нельзя: «195» — это низ или кто-то переписал верх? Для отчёта и
  // для сверки с показаниями станка нужны два числа, а не строка.
  const splitPair = (v) => {
    const parts = String(v ?? '').split(/[/;]/);
    return [(parts[0] || '').trim(), (parts[1] || '').trim()];
  };
  // Эталон и прошлый ввод — ПОДСКАЗКА, а не значение. Раньше они попадали в
  // поля готовыми числами, и оператору оставалось нажать «Начать»: у 24 смен
  // из 33 температура стоит ровно «190/195» — это не показание станка, это
  // подставленный эталон, который никто не переписывал. Такой бланк ничего не
  // говорит о смене, а разбирать по нему брак нечем.
  //
  // Теперь поля пустые, а эталон стоит бледной подсказкой внутри: видно, что
  // должно быть, но записано будет то, что человек прочитал на станке.
  const [refTop, refBot] = splitPair(ref.temp ?? '');

  // Продолжаем свою же смену — показываем то, что записали в прошлый заход.
  // Оператор выходит с позиции и возвращается к ней по нескольку раз за день:
  // станок за это время не остыл, и набирать те же цифры заново — работа
  // впустую, а лишний набор это лишняя опечатка.
  //
  // Начинаем новую — поля пустые. Эталон не подставляем НИКОГДА: именно от
  // подстановки у 24 смен из 33 температура стояла ровно «190/195», и это был
  // не станок, а бланк, который никто не переписывал.
  const back = mode === 'resume';
  const [prevTop, prevBot] = splitPair(back ? (prev.temperature ?? '') : '');
  const [tempTop, setTempTop] = useState(prevTop);
  const [tempBot, setTempBot] = useState(prevBot);
  // Наружу уходит прежней строкой «верх/низ»: на ней стоят сменный бланк и
  // отчёты, и ломать их ради формы ввода незачем.
  const temp = [tempTop, tempBot].filter(x => String(x).trim() !== '').join('/');
  const [hold,  setHold]  = useState(back ? (prev.hold_time_seconds ?? '') : '');
  const [press, setPress] = useState(back ? (prev.pressure ?? '') : '');
  // Параметры КОНКРЕТНОЙ смены — эталона у них нет, каждый раз свои
  const [air,    setAir]    = useState(back ? (prev.air_temp    ?? '') : '');
  const [hum,    setHum]    = useState(back ? (prev.humidity    ?? '') : '');
  const [batch,  setBatch]  = useState(back ? (prev.dsp_batch   ?? '') : '');
  const [pallet, setPallet] = useState(back ? (prev.film_pallet ?? '') : '');
  // Завод плиты и завод плёнки — из справочника: оператор выбирает, а не
  // пишет. «Кроношпан» и «кроношпан» в отчёте по браку обязаны сойтись в одну
  // строку. Номер партии и паллеты рядом остаются текстом — на накладной он
  // каждый раз новый.
  const [dspMaker,  setDspMaker]  = useState(back ? (prev.dsp_maker  ?? '') : '');
  const [filmMaker, setFilmMaker] = useState(back ? (prev.film_maker ?? '') : '');
  const [makers, setMakers] = useState([]);
  useEffect(() => {
    warehouseAPI.getMaterialMakers()
      .then(r => setMakers(r.data.results || r.data || []))
      .catch(() => setMakers([]));
  }, []);
  const makersFor = (kind) => makers.filter(m => m.kind === kind || m.kind === 'both');

  // Партия ДСП — свободная строка: на пачке бывает и номер с буквами, и
  // просто дата поставки. Календарь ставит дату в поле одним касанием, а
  // дописать к ней что угодно можно руками — поле остаётся обычным.
  const dateToField = (iso) => {
    if (!iso) return;
    const [y, m, d] = iso.split('-');
    const stamp = `${d}.${m}.${y}`;
    setBatch(prev => (prev.trim() ? `${prev.trim()} ${stamp}` : stamp));
  };

  // Показания станка. Моноблок у пресса шлёт их сам; оператор набирал те же
  // цифры по памяти — температура верх/низ у 24 смен из 33 стоит ровно
  // «190/195». Подставляем по кнопке, а не молча: человек должен увидеть, что
  // принимает, иначе неисправный датчик уедет в бланк незамеченным.
  const [tele, setTele] = useState(null);
  const [used, setUsed] = useState(false);
  useEffect(() => {
    if (!machine || !machine.id) return;
    productionAPI.getTelemetry(machine.id)
      .then(r => { if (r.data && r.data.has_data) setTele(r.data); })
      .catch(() => {});   // станка у пресса может не быть — это не ошибка смены
  }, [machine]);

  const applyTele = () => {
    if (!tele) return;
    if (tele.temp_top != null)    setTempTop(String(tele.temp_top));
    if (tele.temp_bottom != null) setTempBot(String(tele.temp_bottom));
    if (tele.hold_seconds != null) setHold(String(tele.hold_seconds));
    if (tele.pressure != null)     setPress(String(tele.pressure));
    if (tele.shop_temp != null)    setAir(String(tele.shop_temp));
    if (tele.shop_humidity != null) setHum(String(tele.shop_humidity));
    setUsed(true);
  };

  const INP = {
    width: '100%', padding: '10px 12px', background: 'var(--bg)',
    border: '1px solid var(--border)', borderRadius: 9, color: 'var(--text)',
    fontSize: 16, boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const LBL  = { color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 4 };
  const HINT = { color: '#f59e0b', fontSize: 11, marginTop: 3, fontWeight: 600 };

  // Матн ёки сон бўлиши мумкин — оддий тенглик билан солиштирамиз
  const sameVal = (a, b) => String(a).trim() === String(b).trim();
  const off = (val, etalon) =>
    etalon != null && String(val).trim() !== '' && !sameVal(val, etalon);

  const submit = () => {
    // Верх и низ — две разные плиты со своими нагревателями. Проверка по
    // склеенной строке пропускала бланк с одной заполненной: «190» — это верх
    // или низ, потом уже не скажешь.
    if (String(tempTop).trim() === '' || String(tempBot).trim() === '') {
      toast.error(t('Введите температуру верхней и нижней плиты')); return;
    }
    if (String(hold).trim() === '' || String(press).trim() === '') {
      toast.error(t('Введите время ламинации и давление')); return;
    }
    if (String(air).trim() === '' || String(hum).trim() === '') {
      toast.error(t('Введите температуру и влажность в цехе')); return;
    }
    if (String(batch).trim() === '') {
      toast.error(t('Введите № партии ДСП')); return;
    }
    if (String(pallet).trim() === '') {
      toast.error(t('Введите № паллеты плёнки')); return;
    }
    onConfirm({
      temperature:       String(temp).trim(),
      hold_time_seconds: String(hold).trim(),
      pressure:          String(press).trim(),
      air_temp:          String(air).trim(),
      humidity:          String(hum).trim(),
      dsp_batch:         String(batch).trim(),
      film_pallet:       String(pallet).trim(),
      dsp_maker:         dspMaker || null,
      film_maker:        filmMaker || null,
    });
  };

  const hasRef = ref.matrix || ref.temp != null || ref.hold != null
                 || ref.press != null || ref.thick != null;

  const num = (v) => String(v ?? '').replace(/[^\d.,/]/g, '');

  // Какое поле сейчас заполняют: сам элемент (для позиции курсора), его
  // сеттер и та же обработка ввода, что и при наборе с клавиатуры — иначе
  // клавиатура пускала бы в поле то, что клавиатура не пускает.
  const padEl   = useRef(null);
  const padSet  = useRef(null);
  const padFix  = useRef(null);
  const padCaret = useRef(null);
  // Открыта сразу. У пресса стоит моноблок, другой клавиатуры там нет, и
  // первым касанием оператор всё равно её открывал. А открытие меняло ширину
  // окна — поля разъезжались под пальцем, и следующее нажатие уходило мимо:
  // отсюда и «нажимаю два раза».
  const [padOn, setPadOn] = useState(true);

  const grabField = (el, setter, fix) => {
    padEl.current = el; padSet.current = setter; padFix.current = fix || null;
    setPadOn(true);
  };

  // И по фокусу, и по нажатию. Только фокуса мало: клавиатуру убрали, поле при
  // этом осталось активным — и повторное нажатие по нему события фокуса уже не
  // даёт. Оператор оставался без клавиатуры и без способа её вернуть.
  const padOn_ = (setter, fix) => ({
    onFocus: (e) => grabField(e.target, setter, fix),
    onClick: (e) => grabField(e.target, setter, fix),
  });

  const padKey = (k) => {
    const el = padEl.current, set = padSet.current, fix = padFix.current;
    if (!el || !set) return;
    const val = el.value ?? '';
    const a = el.selectionStart == null ? val.length : el.selectionStart;
    const b = el.selectionEnd == null ? a : el.selectionEnd;
    let next, caret;
    if (k === 'clear') { next = ''; caret = 0; }
    else if (k === 'back') {
      if (a === b && a === 0) return;
      const from = a === b ? a - 1 : a;
      next = val.slice(0, from) + val.slice(b);
      caret = from;
    } else {
      next = val.slice(0, a) + k + val.slice(b);
      caret = a + k.length;
    }
    const fixed = fix ? fix(next) : next;
    set(fixed);
    // Куда поставить курсор — запоминаем, ставим в useLayoutEffect.
    //
    // Раньше это делал requestAnimationFrame, и он опаздывал: React
    // перезаписывает value уже после кадра, а браузер при записи value в
    // сфокусированное поле уводит курсор в конец. Поправишь «190» в середине —
    // и следующая цифра уходит в хвост. С фоновым обновлением очереди раз в
    // двадцать секунд перерисовка могла случиться прямо между нажатием и
    // кадром, и тогда курсор прыгал «сам по себе».
    padCaret.current = caret;
  };

  // Строго после отрисовки: DOM уже с новым значением, и позиция ложится
  // туда, куда её поставил человек.
  useLayoutEffect(() => {
    const pos = padCaret.current;
    if (pos == null || !padEl.current) return;
    padCaret.current = null;
    const el = padEl.current;
    const p = Math.min(pos, (el.value || '').length);
    if (document.activeElement !== el) el.focus();
    try { el.setSelectionRange(p, p); } catch (e) {}
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000000b0', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 12 }}>
      <div style={{
        background: 'var(--card)', borderRadius: 16, padding: 20,
        // Ширина постоянная: раньше она менялась с 660 на 1000 в тот момент,
        // когда открывалась клавиатура, и всё окно разъезжалось под рукой.
        width: '100%', maxWidth: 1000,
        border: '1px solid var(--border)',
      }}>
        {/* Толщина — крупно и первой. Она решает, какую плиту берут со
            стеллажа, а в названии стоит последними двумя цифрами: с двух
            шагов «*16 мм» и «*22 мм» неразличимы. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <PlateBadge thickness={item.ref_thickness} size="big" />
          <div style={{ minWidth: 0 }}>
            <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 17 }}>
              {mode === 'resume' ? t('▶ Продолжить работу') : t('▶ Начать задание')}
            </div>
            <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.product_name} · {item.texture_name}
            </div>
          </div>
        </div>

        {hasRef && (
          <div style={{
            background: '#f59e0b12', border: '1px solid #f59e0b35', borderRadius: 9,
            padding: '7px 11px', fontSize: 12, color: 'var(--text)',
            margin: '11px 0 13px', lineHeight: 1.5,
          }}>
            <b style={{ color: '#f59e0b', letterSpacing: 0.3 }}>{t('ЭТАЛОН')}</b>
            {'   '}{ref.matrix || '—'}
            {'   ·   '}{ref.temp  != null ? ref.temp  + ' °C'      : '—'}
            {'   ·   '}{ref.hold  != null ? ref.hold  + ' ' + t('сек') : '—'}
            {'   ·   '}{ref.press != null ? ref.press + ' ' + t('бар') : '—'}

          </div>
        )}

        {/* Клавиатура — СБОКУ, а не под полями. Снизу она выталкивала кнопку
            «Начать» за край экрана: у пресса монитор невысокий, и оператор
            оказывался в окне, из которого не выйти, не убрав клавиатуру. */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>

        {tele && (
          <div style={{
            background: tele.fresh ? '#0ea5e912' : 'var(--bg)',
            border: '1px solid ' + (tele.fresh ? '#0ea5e945' : 'var(--border)'),
            borderRadius: 9, padding: '8px 11px', marginBottom: 13,
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 12, color: 'var(--text)', flex: 1, minWidth: 0 }}>
              <b style={{ color: tele.fresh ? '#0ea5e9' : 'var(--muted)', letterSpacing: .3 }}>
                {t('СО СТАНКА')}
              </b>
              {'   '}
              {[tele.temp_top, tele.temp_bottom].filter(x => x != null).join(' / ') || '—'} °C
              {'   ·   '}{tele.hold_seconds != null ? tele.hold_seconds + ' ' + t('сек') : '—'}
              {'   ·   '}{tele.pressure != null ? tele.pressure : '—'}
              {'   ·   '}{t('цех')} {tele.shop_temp != null ? tele.shop_temp + '°' : '—'}
              {' / '}{tele.shop_humidity != null ? tele.shop_humidity + '%' : '—'}
              <span style={{ color: 'var(--dim)', marginLeft: 8 }}>
                {tele.fresh ? '' : t('· показание старое, ') }
                {tele.age_minutes < 1 ? t('только что')
                  : Math.round(tele.age_minutes) + ' ' + t('мин назад')}
              </span>
            </div>
            <button type="button" onClick={applyTele} style={{
              background: used ? 'var(--bg)' : '#0ea5e9', color: used ? 'var(--muted)' : '#fff',
              border: '1px solid ' + (used ? 'var(--border)' : '#0ea5e9'),
              borderRadius: 8, padding: '7px 13px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}>{used ? t('✓ подставлено') : t('Подставить')}</button>
          </div>
        )}

        {/* Пресс: эталон есть — отклонение подсвечиваем */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 11 }}>
          <div style={{ minWidth: 0 }}>
            <label style={LBL}>{t('🌡 Темп. ВЕРХ, °C')}</label>
            <input type="text" value={tempTop} onChange={e => setTempTop(num(e.target.value))}
              {...padOn_(setTempTop, num)}
              placeholder={refTop || '190'} style={INP} autoFocus />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={LBL}>{t('🌡 Темп. НИЗ, °C')}</label>
            <input type="text" value={tempBot} onChange={e => setTempBot(num(e.target.value))}
              {...padOn_(setTempBot, num)}
              placeholder={refBot || refTop || '195'} style={INP} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={LBL}>{t('⏱ Время ламинации, сек')}</label>
            <input type="text" value={hold} onChange={e => setHold(num(e.target.value))}
              {...padOn_(setHold, num)}
              placeholder={ref.hold != null ? String(ref.hold) : '18'} style={INP} />
            {off(hold, ref.hold) && <div style={HINT}>⚠ {t('эталон')} {ref.hold}</div>}
          </div>
          {/* Давление у пресса ОДНО: плита смыкается одним усилием, верх и низ
              тут не разделяются. Станок его так и присылает — одним числом,
              рядом с двумя температурами. */}
          <div style={{ minWidth: 0 }}>
            <label style={LBL}>{t('💪 Давление, МПа')}</label>
            <input type="text" value={press} onChange={e => setPress(num(e.target.value))}
              {...padOn_(setPress, num)}
              placeholder={ref.press != null ? String(ref.press) : '12'} style={INP} />
            {off(press, ref.press) && <div style={HINT}>⚠ {t('эталон')} {ref.press}</div>}
          </div>
        </div>
        {off(temp, ref.temp) && (
          <div style={{ ...HINT, marginTop: -6, marginBottom: 10 }}>
            ⚠ {t('эталон температуры')} {ref.temp}
          </div>
        )}

        {/* Цех и сырьё: эталона нет, каждую смену своё.
            Температуру и влажность раньше писали в одно поле «20.65» — в бланке
            это две графы, и разобрать их обратно из одной строки нельзя. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <label style={LBL}>{t('🌡 Темп. в цехе')}</label>
            <input type="text" inputMode="decimal" value={air}
              {...padOn_(setAir, num)}
              onChange={e => setAir(num(e.target.value))} placeholder="20" style={INP} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={LBL}>{t('💧 Влажность')}</label>
            <input type="text" inputMode="decimal" value={hum}
              {...padOn_(setHum, num)}
              onChange={e => setHum(num(e.target.value))} placeholder="65" style={INP} />
          </div>
        </div>

        {/* Материал: сначала ЧЕЙ, потом КАКАЯ партия. Так это и читают с
            накладной, и так же стоит в паспорте листа. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <label style={LBL}>{t('🏭 Завод плиты')}</label>
            <select value={dspMaker} onChange={e => setDspMaker(e.target.value)}
              style={{ ...INP, cursor: 'pointer' }}>
              <option value="">{t('— не указан —')}</option>
              {makersFor('dsp').map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={LBL}>{t('🏷 Партия ДСП')}</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input type="text" autoComplete="off" value={batch}
                {...padOn_(setBatch, null)}
                onChange={e => setBatch(e.target.value)}
                placeholder={t('номер или дата')} style={{ ...INP, flex: 1, minWidth: 0 }} />
              {/* Календарь — второе поле, а не маска поверх первого: маска
                  выбрасывала буквы, а на пачке они бывают. */}
              <label className="touch-btn" title={t('Взять дату из календаря')}
                style={{
                  ...INP, width: 52, flexShrink: 0, display: 'grid',
                  placeItems: 'center', cursor: 'pointer', padding: 0,
                }}>
                📅
                <input type="date" onChange={e => dateToField(e.target.value)}
                  style={{ position: 'absolute', width: 1, height: 1, opacity: 0,
                           pointerEvents: 'none' }} />
              </label>
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={LBL}>{t('🏭 Завод плёнки')}</label>
            <select value={filmMaker} onChange={e => setFilmMaker(e.target.value)}
              style={{ ...INP, cursor: 'pointer' }}>
              <option value="">{t('— не указан —')}</option>
              {makersFor('film').map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={LBL}>{t('🎞 Паллета плёнки')}</label>
            <input type="text" autoComplete="off" value={pallet}
              {...padOn_(setPallet, null)}
              onChange={e => setPallet(e.target.value)}
              placeholder={t('номер')} style={INP} />
          </div>
        </div>

        {/* Явная кнопка: убрали клавиатуру — видно, чем вернуть */}
        {!padOn && (
          <button type="button" onClick={() => setPadOn(true)} className="touch-btn" style={{
            width: '100%', background: 'var(--bg)', border: '1px dashed var(--border)',
            borderRadius: 10, color: 'var(--muted)', padding: '10px 0', marginBottom: 10,
            fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}>⌨ {t('Показать клавиатуру')}</button>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={submit} disabled={saving} style={{ flex: 2, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 0', fontSize: 16, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '...' : (mode === 'resume' ? t('▶ Продолжить') : t('▶ Начать'))}
          </button>
          <button onClick={onCancel} style={{ flex: 1, background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 10, padding: '13px 0', fontSize: 16, cursor: 'pointer' }}>
            {t('Отмена')}
          </button>
        </div>

        </div>
        {padOn && (
          <div style={{ width: 320, flexShrink: 0 }}>
            <NumPad onKey={padKey} onClose={() => setPadOn(false)} />
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function CompleteJobModal({ onConfirm, onCancel, stopping, done, plan, good, stats }) {
  const [selectedIds, setSelectedIds] = useState([]);

  // { nom_id: { name, unit, entries: [StockEntry] } } — фақат мусбат қолдиқли
  const [grouped,       setGrouped]       = useState({});
  const [loadingBatches, setLoadingBatches] = useState(true);

  useEffect(() => {
    Promise.all([
      warehouseAPI.getNomenclatures({ type: 'raw' }),
      warehouseAPI.getStockEntries(),
    ]).then(([nomRes, entryRes]) => {
      const nomMap = Object.fromEntries(nomRes.data.map(n => [n.id, n]));
      const result = {};
      entryRes.data
        .filter(e => parseFloat(e.quantity) > 0)
        .forEach(entry => {
          const nom = nomMap[entry.nomenclature];
          if (!nom) return;
          if (!result[entry.nomenclature]) {
            result[entry.nomenclature] = { name: nom.name, unit: nom.unit, entries: [] };
          }
          result[entry.nomenclature].entries.push(entry);
        });
      setGrouped(result);
    }).catch(() => {}).finally(() => setLoadingBatches(false));
  }, []);

  const toggle = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // План не выполнен — это две разные вещи, и знает разницу только оператор:
  //   «позиция закрыта»  — заказчику хватило и меньшего;
  //   «смена кончилась»  — остаток допрессуют позже.
  // Раньше «Завершить» всегда закрывало позицию, и недоделанная работа
  // исчезала из списка навсегда — приходилось заводить новый заказ.
  // План закрывается высшим сортом: 34 выпущено при 4 пересортированных —
  // это 30 годных, а не перевыполнение
  const topGood = (good === undefined || good === null) ? done : good;
  const shortOfPlan = plan > 0 && topGood < plan;

  const handleConfirm = (finishPosition) => {
    const payload = { quantity_defect: 0, finish_position: finishPosition };
    if (selectedIds.length > 0) payload.raw_material_batch_ids = selectedIds;
    onConfirm(payload);
  };

  const hasBatches = Object.keys(grouped).length > 0;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000000cc',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 300, padding: 20,
    }}>
      <div style={{
        background: 'var(--card)', borderRadius: 20, padding: 24,
        width: '100%', maxWidth: 900,
        maxHeight: '94vh', overflowY: 'auto',
      }}>
        {/* Сарлавҳа */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700 }}>{t('⏹ Завершить задание')}</h2>
          <button onClick={onCancel} style={{
            background: 'none', border: 'none', color: 'var(--dim)', fontSize: 22, cursor: 'pointer',
          }}>✕</button>
        </div>

        {/* Две колонки: слева арифметика смены, справа партии и кнопки. В одну
            колонку окно уезжало под вертикальный скролл, и до «Позиция закрыта»
            надо было прокручивать, стоя у пресса. */}
        <div className="complete-grid">
        <div className="complete-col">

        {/* Что уходит в отчёт. Раньше окно называло одно число — сколько раз
            нажали «+1», — и сорта оператор проверял, только вернувшись на экран
            брака. Ошибку замечали уже после закрытия позиции.

            Порядок строк — это арифметика смены: высший плюс 1/2/3 сорт даёт
            то, что вышло из пресса. Брак материала стоит ОТДЕЛЬНЫМ блоком: его
            отмечают до пресса, листа тогда ещё нет, и в эту сумму он не входит.
            Вперемешку строки не сходились в уме и читались как ошибка. */}
        {stats && (() => {
          const sheetRows = stats.rows.filter(r => r.scope !== 'material' && r.count > 0);
          const matRows   = stats.rows.filter(r => r.scope === 'material' && r.count > 0);
          const Row = ({ label, value, strong, color, size }) => (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
              <span style={{
                color: strong ? 'var(--text)' : 'var(--muted)',
                fontSize: strong ? 14 : 13, fontWeight: strong ? 700 : 400, flex: 1,
              }}>{label}</span>
              <b style={{ color: color || 'var(--text)', fontSize: size || 16 }}>{value}</b>
            </div>
          );
          return (
            <div style={{
              background: 'var(--bg)', border: '1px solid var(--border)',
              borderRadius: 12, padding: '12px 16px', marginBottom: 14,
            }}>
              <div style={{
                color: 'var(--dim)', fontSize: 11, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8,
              }}>{t('Итог смены по этой позиции')}</div>

              <Row label={t('1-й сорт')} value={stats.top} strong color="#22c55e" size={26} />
              {sheetRows.map(r => <Row key={r.id} label={r.name} value={r.count} />)}

              <div style={{
                marginTop: 6, paddingTop: 8, borderTop: '2px solid var(--border)',
                display: 'flex', alignItems: 'baseline', gap: 8,
              }}>
                <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 700, flex: 1 }}>
                  {t('Всего вышло из пресса')}
                </span>
                <b style={{ color: 'var(--text)', fontSize: 20 }}>{stats.produced}</b>
              </div>

              {matRows.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed var(--border)' }}>
                  <div style={{
                    color: 'var(--dim)', fontSize: 11, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
                  }}>Брак материала · до пресса, в сумму выше не входит</div>
                  {matRows.map(r => <Row key={r.id} label={r.name} value={r.count} />)}
                </div>
              )}

              {sheetRows.length === 0 && matRows.length === 0 && (
                <div style={{ color: 'var(--dim)', fontSize: 13, marginTop: 6 }}>{t('Брака нет')}</div>
              )}

              <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 10, lineHeight: 1.4 }}>
                Не сходится — закройте окно и поправьте кнопкой «⚠ БРАК».
              </div>
            </div>
          );
        })()}

        </div>
        <div className="complete-col">

        {/* Спрашиваем ВСЕГДА, даже когда план выполнен: «Завершить» закрывает
            позицию насовсем, а нажать её можно и не глядя. Разница только в
            тексте — при выполненном плане закрыть логично, но подтвердить всё
            равно нужно. */}
        <div style={{
          background: shortOfPlan ? '#f59e0b18' : '#22c55e18',
          border: '1px solid ' + (shortOfPlan ? '#f59e0b40' : '#22c55e40'),
          borderRadius: 12, padding: '12px 16px', marginBottom: 14,
        }}>
          <div style={{ color: shortOfPlan ? '#f59e0b' : '#22c55e', fontSize: 15, fontWeight: 700 }}>
            {shortOfPlan
              ? `${t('План не выполнен')}: ${topGood} ${t('из')} ${plan}`
              : `${t('План выполнен')}: ${topGood} ${t('из')} ${plan}`}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
            {shortOfPlan
              ? t('Остаток допрессуют позже — или позиция закрывается как есть?')
              : t('Позиция будет закрыта и исчезнет из списка. Если ещё прессуете — не закрывайте.')}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 24 }}>
          {/* Хомашё партиялари */}
          {loadingBatches ? (
            <div style={{ color: 'var(--dim)', fontSize: 13, textAlign: 'center', padding: '8px 0' }}>
              {t('Загрузка партий...')}
            </div>
          ) : hasBatches && (
            <div>
              <label style={{ display: 'block', color: 'var(--muted)', fontSize: 13, marginBottom: 8 }}>
                📦 Использованные партии сырья — <span style={{ color: 'var(--border)' }}>{t('необязательно')}</span>
              </label>
              <div style={{
                maxHeight: 220, overflowY: 'auto',
                border: '1px solid var(--border)', borderRadius: 10,
                background: 'var(--bg)',
              }}>
                {Object.entries(grouped).map(([nomId, group]) => (
                  <div key={nomId} style={{ borderBottom: '1px solid var(--border)' }}>
                    {/* Номенклатура сарлавҳаси */}
                    <div style={{
                      padding: '7px 12px',
                      fontSize: 11, fontWeight: 700,
                      color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: 0.5,
                      background: 'var(--card)',
                    }}>
                      {group.name}
                    </div>
                    {/* Партиялар */}
                    {group.entries.map(entry => {
                      const checked  = selectedIds.includes(entry.id);
                      const label    = entry.batch_number
                        ? `Партия: ${entry.batch_number}`
                        : entry.created_at.slice(0, 10);
                      return (
                        <label key={entry.id} style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '10px 14px', cursor: 'pointer',
                          background: checked ? 'var(--accent-weak)' : 'transparent',
                          borderLeft: checked ? '3px solid var(--accent)' : '3px solid transparent',
                          transition: 'background 0.15s',
                        }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(entry.id)}
                            style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--accent)' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: checked ? 700 : 400 }}>
                              {label}
                            </div>
                          </div>
                          <div style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {parseFloat(entry.quantity).toLocaleString('ru')} {group.unit}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
              {selectedIds.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 6, fontWeight: 600 }}>
                  ✓ Выбрано партий: {selectedIds.length}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Тугмалар */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={onCancel} disabled={stopping} style={{
            flex: 1, minWidth: 120, padding: '14px 0', background: 'transparent',
            border: '1px solid var(--border)', borderRadius: 12,
            color: 'var(--dim)', fontSize: 16, fontWeight: 600, cursor: 'pointer',
          }}>{t('Отмена')}</button>

          {/* Не закрывать: при невыполненном плане остаток допрессуют позже,
              при выполненном — оператор может продолжать и сверх плана.
              В обоих случаях работа остаётся в списке. */}
          <button onClick={() => handleConfirm(false)} disabled={stopping} style={{
            flex: 2, minWidth: 200, padding: '14px 10px',
            background: stopping ? 'var(--card)' : 'var(--accent)',
            border: 'none', borderRadius: 12,
            color: stopping ? 'var(--mid)' : '#fff',
            fontSize: 15, fontWeight: 700, cursor: stopping ? 'not-allowed' : 'pointer',
          }}>
            {stopping ? '…'
                      : (shortOfPlan ? `⏸ ${t('Смена окончена — остаток позже')}`
                                     : `⏸ ${t('Не закрывать — ещё продолжу')}`)}
          </button>

          <button onClick={() => handleConfirm(true)} disabled={stopping} style={{
            flex: 2, minWidth: 200, padding: '14px 10px',
            background: stopping ? 'var(--card)' : '#ef4444',
            border: 'none', borderRadius: 12,
            color: stopping ? 'var(--mid)' : '#fff',
            fontSize: 15, fontWeight: 700, cursor: stopping ? 'not-allowed' : 'pointer',
          }}>
            {stopping ? t('Завершаем...') : `✅ ${t('Позиция закрыта')}`}
          </button>
        </div>

        </div>
        </div>
      </div>
    </div>
  );
}

// Окно живёт своей жизнью, пока в нём набирают. Родительский экран опрашивает
// очередь и незавершённые смены каждые двадцать секунд, и каждый ответ гнал
// перерисовку сюда — прямо в момент набора. Пропускаем её: снаружи меняется
// только «сохраняем», всё остальное окно держит у себя.
const PressParamsModal = React.memo(PressParamsModalBase);

// ── Брак ──────────────────────────────────────────────────────────────────────
// Три вещи, которые оператор отмечает прямо у пресса:
//   ЛДСП   — лист отпрессован, но ушёл не высшим сортом (1/2/3);
//   плёнка — брак увидели ДО пресса, лист не делали;
//   ДСП    — плита не годится в работу.
// Плёнка и ДСП годный выпуск не уменьшают — считает бэкенд, здесь только счёт.
// Промахнулся по кнопке — «−1» тут же рядом, пока задание не завершено.
function DefectSection({ queueItemId, version, planned, onStats }) {
  const [open, setOpen]       = useState(false);
  const [jobId, setJobId]     = useState(null);
  const [types, setTypes]     = useState([]);
  const [counts, setCounts]   = useState({});   // grade_type_id -> quantity
  // Предел разбраковки считает бэкенд: выпущено по позиции заказа минус то,
  // что уже отмечено сортами. План тут ни при чём — выпустили 8 при плане 5,
  // значит и отметить можно до 8.
  const [produced, setProduced] = useState(0);
  const [marked, setMarked]     = useState(0);
  const [busy, setBusy]       = useState(false);

  // Форма записей отличается у двух эндпоинтов — приводим к одному виду
  const toCounts = (list) => {
    const out = {};
    (list || []).forEach(e => {
      const id = e.grade_type_id !== undefined ? e.grade_type_id : e.grade_type;
      out[id] = e.quantity;
    });
    return out;
  };

  // Сессию берём по ЗАДАНИЮ, а не «любую открытую на прессе». Раньше брали по
  // прессу: если на нём висела ещё одна незакрытая сессия, отметки уходили в
  // неё, а экран читал другую — цифры пропадали при возврате.
  const load = useCallback(() => {
    if (!queueItemId) return;
    Promise.all([
      productionAPI.getPressJobs({ queue_item: queueItemId, active: 'true' }),
      gradeAPI.getGradeTypes({ active: 'true' }),
    ]).then(([j, g]) => {
      const list = j.data.results || j.data || [];
      const job  = list[0] || null;
      setJobId(job ? job.id : null);
      // Именно totals: «высший сорт» считается по всей позиции, и строки
      // сортов должны считаться так же — иначе на одном прессе с двумя
      // моноблоками сумма не сходится с выпуском.
      setCounts(toCounts(job && (job.grade_totals || job.grade_entries)));
      setProduced(job ? (job.sheet_produced || 0) : 0);
      setMarked(job ? (job.sheet_marked || 0) : 0);
      setTypes((g.data || []).filter(x => !x.is_default));
    }).catch(() => {});
  }, [queueItemId]);

  useEffect(() => { load(); }, [load, version]);

  // Сводку отдаём наверх целиком: экран выпуска считает по ней план, а окно
  // завершения показывает её оператору перед закрытием позиции.
  // Хук стоит ДО раннего выхода по !jobId — React не допускает условных хуков.
  const topLeft = Math.max(0, produced - marked);
  const statsKey = [produced, marked, types.map(x => `${x.id}:${counts[x.id] || 0}`).join(',')].join('|');
  useEffect(() => {
    if (!onStats) return;
    onStats({
      top: topLeft,
      produced,
      rows: types.map(x => ({
        id: x.id, name: x.name, scope: x.scope, count: counts[x.id] || 0,
      })),
    });
    // statsKey — примитив: объект в зависимостях гонял бы эффект каждый рендер
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsKey, onStats]);

  const change = async (typeId, delta) => {
    if (!jobId || busy) return;
    setBusy(true);
    try {
      const r = delta > 0
        ? await productionAPI.markGrade(jobId, typeId)
        : await productionAPI.unmarkGrade(jobId, typeId);
      setCounts(toCounts(r.data.grade_entries));
      setProduced(r.data.sheet_produced || 0);
      setMarked(r.data.sheet_marked || 0);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setBusy(false); }
  };

  // У каждой кнопки свой цвет: оператор бьёт по ним не глядя, и одинаковые
  // красные плитки различались только подписью — а подпись в спешке не читают.
  //
  // Сорта идут по тяжести: 1-й жёлтый, 2-й оранжевый, 3-й красный — цвет
  // сам подсказывает, насколько лист просел.
  const SHEET_COLORS    = ['#eab308', '#f97316', '#ef4444', '#ec4899', '#a855f7'];
  // Плёнку и ДСП закрепляем по названию, а не по порядку: технолог добавит
  // новый вид брака — и цвета всей смене пришлось бы переучивать.
  //
  // Малиновый и синий, а не соседние оттенки: фиолетовый с голубым в цеху при
  // ярком свете и под углом сливались. Эти два не спутать и боковым зрением.
  const COLOR_FILM = '#ec4899';   // плёнка — малиновый
  const COLOR_DSP  = '#3b82f6';   // ДСП — синий
  const MATERIAL_COLORS = ['#22c55e', '#14b8a6', '#a855f7', '#f59e0b'];

  const sheetTypes    = types.filter(x => x.scope === 'sheet');
  const materialTypes = types.filter(x => x.scope === 'material');

  // Тот самый сорт, в который уходит нажатие «БРАК». Ищем по названию, а не
  // по порядку: технолог переставит список — привязка по номеру уехала бы на
  // соседний сорт и брак начал бы записываться вторым сортом.
  const defectType  = sheetTypes.find(x => (x.name || '').toLowerCase().includes('брак'))
                      || null;
  const defectCount = defectType ? (counts[defectType.id] || 0) : 0;

  // Плёнка. Её не отмечают по одной: рулон рвётся в начале смены, и оператор
  // видит сразу пачку испорченных листов. Пять нажатий он не сделает, а
  // сделает — собьётся со счёта. Поэтому число.
  const filmType = materialTypes.find(x => {
    const n = (x.name || '').toLowerCase();
    return n.includes('плён') || n.includes('плен');
  }) || null;
  const filmSaved = filmType ? (counts[filmType.id] || 0) : 0;

  // Черновик поля отдельно от записанного: сохранять на каждую цифру нельзя —
  // набирая «12», человек на мгновение проходит через «1», и в смену уехала бы
  // единица.
  const [film, setFilm] = useState('');
  const [filmTouched, setFilmTouched] = useState(false);
  useEffect(() => {
    // Не трогали — показываем то, что в смене. Тронули — не перебиваем набор
    // ответом сервера: обновление приходит раз в несколько секунд и стирало бы
    // набранное прямо под рукой.
    if (!filmTouched) setFilm(filmSaved ? String(filmSaved) : '');   // ноль — пусто
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filmSaved]);

  // «Записать» относится только к набранному руками. Раньше условием была
  // просто разница с записанным, и кнопка вспыхивала на каждое нажатие «+1»:
  // поле уже изменилось, ответ сервера ещё не пришёл — полсекунды разницы, и
  // кнопка успевала мелькнуть.
  const filmDirty = !!filmType && filmTouched && Number(film || 0) !== filmSaved;

  // Шаг кнопкой. Одну-две плёнки удобнее отметить нажатием — и записывается
  // оно сразу, без второго действия: тут нечего перепроверять, цифра
  // изменилась ровно на единицу.
  // Шаг считаем от НАБРАННОГО, если в поле правили, иначе от записанного.
  // От записанного всегда было бы неверно: набрал 8, нажал «+1» — ожидаешь 9,
  // а получил бы «записанное плюс один».
  const stepFilm = (delta) => {
    const base = filmTouched ? Number(film || 0) : filmSaved;
    return sendFilm(Math.max(0, base + delta));
  };

  // Ответ сервера — истина. Поле выставляем по нему, а не по тому, что
  // посчитали у себя: иначе после быстрых нажатий на экране одно число, в
  // смене другое, и понять, какое из них настоящее, уже нельзя.
  const sendFilm = async (qty) => {
    if (!jobId || !filmType || busy) return;
    setBusy(true);
    try {
      const r = await productionAPI.setGrade(jobId, filmType.id, qty);
      setCounts(toCounts(r.data.grade_entries));
      setFilm(qty ? String(qty) : '');
      setFilmTouched(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setBusy(false); }
  };

  const saveFilm = () => sendFilm(Number(film || 0));

  const colorMap = {};
  sheetTypes.forEach((s, i) => { colorMap[s.id] = SHEET_COLORS[i % SHEET_COLORS.length]; });
  materialTypes.forEach((m, i) => {
    const n = (m.name || '').toLowerCase();
    if (n.includes('плён') || n.includes('плен')) colorMap[m.id] = COLOR_FILM;
    else if (n.includes('дсп')) colorMap[m.id] = COLOR_DSP;
    else colorMap[m.id] = MATERIAL_COLORS[i % MATERIAL_COLORS.length];
  });

  const colorOf = (ty) => colorMap[ty.id] || (ty.scope === 'material' ? COLOR_FILM : '#ef4444');
  const totalDefects = Object.values(counts).reduce((s, n) => s + (n || 0), 0);

  // Сорта ЛДСП — одним рядом, брак материала — тоже одним: плёнка и ДСП
  // занимали по отдельной строке и уводили экран вниз на пустом месте.
  //
  // Заголовок был «брак по сортам», а оператор сорт не ставит: он считает
  // листы, вышедшие не высшими, и на этом всё — какой это сорт, разберёт
  // склад на приёмке. Заголовок обещал работу, которой у пресса нет.
  const groups = [
    ...(sheetTypes.length
      ? [{ title: 'ЛДСП — не 1-й сорт (сорт поставит склад)', items: sheetTypes }]
      : []),
    ...(materialTypes.length
      ? [{ title: 'Брак материала', items: materialTypes, material: true }]
      : []),
  ];
  // Разбраковать можно только то, что уже вышло из пресса
  const sheetLeft = Math.max(0, produced - marked);
  // План смены закрывается высшим сортом, а не счётчиком выпуска: тот считает
  // и то, что потом пересортировали
  const planMet = planned > 0 && sheetLeft >= planned;

  if (!jobId) return null;

  return (
    <>
      {/* Сводка стоит СБОКУ — это правая колонка рабочего экрана.
          Экран разложен в три столбца именно поэтому: ничего не растёт
          вниз и до «+1 ГОТОВО» не нужно доскроливать. */}
      <div className="defect-rail">
        <div style={{
          background: 'var(--card)', borderRadius: 16, padding: 14,
          border: `1px solid ${totalDefects ? '#ef444440' : 'var(--border)'}`,
        }}>
          {/* Не «Брак»: первой строкой тут стоит высший сорт — то, ради чего
              смена и работает. Раньше блок показывал только потери, и сколько
              листов осталось годными, оператор считал в уме. */}
          <div style={{
            color: 'var(--muted)', fontSize: 13, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10,
          }}>{t('Сорта')}</div>

          {/* Высший сорт = выпущено минус то, что уже пересортировали.
              Брак плёнки и ДСП сюда не входит: его отмечают до пресса.

              Крупно и против плана: план смены считают именно по высшему
              сорту, а не по счётчику в центре — тот показывает всё, что вышло
              из пресса, вместе с браком. Оператор сверял это в уме. */}
          <div style={{
            marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid var(--border)',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 700, marginBottom: 2 }}>
              {t('1-й сорт')}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <b style={{
                fontSize: 52, lineHeight: 1, fontWeight: 900,
                color: planMet ? '#22c55e' : (sheetLeft > 0 ? 'var(--text)' : 'var(--mid)'),
              }}>{sheetLeft}</b>
              {planned > 0 && (
                <>
                  <span style={{ color: 'var(--border)', fontSize: 26, fontWeight: 300 }}>/</span>
                  <span style={{ color: 'var(--mid)', fontSize: 26, fontWeight: 700 }}>{planned}</span>
                </>
              )}
            </div>
            {planned > 0 && (
              <div style={{
                marginTop: 4, fontSize: 12, fontWeight: 700,
                color: planMet ? '#22c55e' : '#f59e0b',
              }}>
                {planMet ? '✅ План выполнен' : `Осталось ${planned - sheetLeft} шт`}
              </div>
            )}
          </div>

          {/* Брак — одной строкой и одной кнопкой.
              Раньше тут стояли все сорта и оба вида брака материала, а по
              нажатию открывался экран с пятью плитками: 1-й, 2-й, 3-й сорт,
              брак плёнки, брак ДСП. Оператор в рукавицах, лист горячий, и
              выбор «какой это сорт» делался на глаз за полсекунды — а сорт
              потом всё равно переставлял склад, глядя на лист под нормальным
              светом. Плёнку и ДСП там же пересчитывают руками по пачке.

              Значит у пресса остаётся один вопрос: годный или нет. Ответ —
              одна кнопка, мимо которой не промахнёшься. */}
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4,
          }}>
            <span style={{ fontSize: 14, color: 'var(--dim)', flex: 1 }}>
              {t('Брак')}
            </span>
            <b style={{ fontSize: 30, color: defectCount ? '#ef4444' : 'var(--mid)' }}>
              {defectCount}
            </b>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button onClick={() => defectType && change(defectType.id, +1)}
              disabled={!defectType || busy || sheetLeft <= 0}
              className="touch-btn"
              style={{
                flex: 1, height: 64,
                background: (!defectType || sheetLeft <= 0) ? 'var(--bg)' : '#ef4444',
                border: (!defectType || sheetLeft <= 0) ? '2px solid var(--border)' : 'none',
                borderRadius: 14,
                color: (!defectType || sheetLeft <= 0) ? 'var(--mid)' : '#fff',
                fontSize: 19, fontWeight: 900,
                cursor: (!defectType || sheetLeft <= 0) ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
              }}>⚠ {t('БРАК')} +1</button>

            {/* Промахнулся — снимается тут же, пока задание не закрыто */}
            <button onClick={() => defectType && change(defectType.id, -1)}
              disabled={!defectType || busy || !defectCount}
              className="touch-btn"
              style={{
                width: 64, height: 64, background: 'var(--bg)',
                border: '2px solid var(--border)', borderRadius: 14,
                color: defectCount ? 'var(--muted)' : 'var(--mid)',
                fontSize: 24, fontWeight: 800,
                cursor: defectCount ? 'pointer' : 'not-allowed',
              }}>−1</button>
          </div>

          {sheetLeft <= 0 && defectType && (
            <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 7, lineHeight: 1.4 }}>
              {t('Сначала выпустите лист — бракуют то, что вышло из пресса')}
            </div>
          )}

          {/* Брак плёнки — числом. Это то, что видел оператор; итоговую цифру
              скажет склад: он держит пачку в руках и пересчитывает. Выпуск она
              не уменьшает — плёнку бракуют ДО пресса, листа из неё не вышло. */}
          {filmType && (
            <div style={{
              marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)',
            }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 7,
              }}>
                <span style={{ color: 'var(--dim)', fontSize: 14, flex: 1 }}>
                  {filmType.name}
                </span>
                <b style={{ fontSize: 24, color: filmSaved ? '#ec4899' : 'var(--mid)' }}>
                  {filmSaved}
                </b>
              </div>

              {/* И кнопка, и поле. Поле записывается само — при уходе из него
                  или по Enter: отдельное действие «сохранить» у пресса лишнее,
                  там набрали цифру и пошли к станку, а несохранённое число не
                  существует. Кнопка «Записать» остаётся видимой подсказкой,
                  пока цифру правят руками.

                  Раньше. Одну плёнку отметить нажатием быстрее, а
                  когда рулон порвался и их сразу восемь — быстрее написать
                  число. Кнопка пишет сразу: цифра изменилась ровно на
                  единицу, перепроверять нечего. Набранное руками — по
                  «Записать»: набирая «12», человек на мгновение проходит
                  через «1», и эта единица уехала бы в смену. */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button onClick={() => stepFilm(-1)}
                  disabled={busy || Number(film || 0) <= 0}
                  className="touch-btn"
                  style={{
                    width: 54, height: 54, flexShrink: 0, borderRadius: 12,
                    background: 'var(--bg)', border: '2px solid var(--border)',
                    color: Number(film || 0) > 0 ? 'var(--muted)' : 'var(--mid)',
                    fontSize: 22, fontWeight: 800, fontFamily: 'inherit',
                    cursor: Number(film || 0) > 0 ? 'pointer' : 'not-allowed',
                  }}>−1</button>

                <input
                  type="text" inputMode="numeric" value={film}
                  onChange={e => { setFilmTouched(true);
                                   setFilm(e.target.value.replace(/[^0-9]/g, '').slice(0, 4)); }}
                  onFocus={e => e.target.select()}
                  onBlur={() => { if (filmDirty) saveFilm(); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                  placeholder="0"
                  style={{
                    flex: 1, minWidth: 0, height: 54, textAlign: 'center',
                    background: 'var(--bg)', border: '2px solid var(--border)',
                    borderRadius: 12, color: 'var(--text)',
                    fontSize: 24, fontWeight: 800, fontFamily: 'inherit',
                    outline: 'none', boxSizing: 'border-box',
                  }} />

                <button onClick={() => stepFilm(+1)} disabled={busy}
                  className="touch-btn"
                  style={{
                    width: 54, height: 54, flexShrink: 0, borderRadius: 12,
                    background: '#ec4899', border: 'none', color: '#fff',
                    fontSize: 22, fontWeight: 800, fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}>+1</button>
              </div>

              {filmDirty && (
                <button onClick={saveFilm} disabled={busy} className="touch-btn"
                  style={{
                    width: '100%', height: 48, marginTop: 7, borderRadius: 12,
                    background: '#ec4899', border: 'none', color: '#fff',
                    fontSize: 15, fontWeight: 800, fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}>{t('Записать')} {Number(film || 0)}</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Экран брака — крупные кнопки, попадать пальцем в рукавице */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'var(--bg)', zIndex: 1300,
          display: 'flex', flexDirection: 'column', padding: 20, overflowY: 'auto',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: 'var(--text)' }}>
              ⚠ {t('Брак')}
            </div>
            <button onClick={() => setOpen(false)} className="touch-btn"
              style={{
                marginLeft: 'auto', height: 64, padding: '0 28px',
                background: 'var(--accent)', border: 'none', borderRadius: 16,
                color: '#fff', fontSize: 19, fontWeight: 800, cursor: 'pointer',
              }}>← {t('НАЗАД')}</button>
          </div>

          {/* Сколько ЛДСП уже разбраковано: больше выпущенного не отметишь */}
          <div style={{
            background: sheetLeft > 0 ? 'var(--card)' : '#ef444418',
            border: `1px solid ${sheetLeft > 0 ? 'var(--border)' : '#ef444440'}`,
            borderRadius: 12, padding: '10px 16px', marginBottom: 16,
            color: 'var(--muted)', fontSize: 16,
          }}>
            {t('Выпущено листов:')} <b style={{ color: 'var(--text)' }}>{produced}</b>
            {' · '}{t('отмечено сортами:')} <b style={{ color: 'var(--text)' }}>{marked}</b>
            {sheetLeft > 0
              ? <> · {t('можно ещё')} <b style={{ color: '#22c55e' }}>{sheetLeft}</b></>
              : <> · <b style={{ color: '#ef4444' }}>{t('больше нельзя — сначала выпустите лист')}</b></>}
          </div>

          {/* Каждый вид брака — своим рядом: ЛДСП, плёнка, ДСП */}
          {groups.map(gr => (
            <div key={gr.title} style={{ marginBottom: 16 }}>
              <div style={{ color: 'var(--muted)', fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
                {gr.title}
                {gr.material && (
                  <span style={{ color: 'var(--dim)', fontSize: 12, fontWeight: 400 }}>
                    {' '}· {t('до пресса, выпуск не уменьшает')}
                  </span>
                )}
              </div>
              {/* Карточки одинаковой ширины и стоят слева: раньше они
                  растягивались на всю строку, и «+1» плёнки был втрое
                  шире «+1» сорта — рука привыкает к месту, а оно плывёт. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {gr.items.map(ty => {
                  const capped = ty.scope === 'sheet' && sheetLeft <= 0;
                  return (
                    <div key={ty.id} style={{
                      width: 300, background: 'var(--card)', borderRadius: 16, padding: 12,
                      border: `2px solid ${colorOf(ty)}30`,
                    }}>
                      <div style={{
                        // Подпись в цвет своей кнопки: плёнку от ДСП видно
                        // до того, как палец опустился
                        color: colorOf(ty), fontSize: 15, fontWeight: 800, marginBottom: 8,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>{ty.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button onClick={() => change(ty.id, +1)} disabled={busy || capped}
                          className="touch-btn"
                          title={capped ? t('Больше выпущенного отметить нельзя') : undefined}
                          style={{
                            width: 148, height: 72,
                            background: capped ? 'transparent' : colorOf(ty),
                            border: capped ? '2px solid var(--border)' : 'none',
                            borderRadius: 14, color: capped ? 'var(--dim)' : '#fff',
                            fontSize: 30, fontWeight: 900,
                            cursor: (busy || capped) ? 'not-allowed' : 'pointer',
                            opacity: busy ? 0.6 : 1,
                          }}>+1</button>
                        <div style={{
                          width: 52, textAlign: 'center', fontSize: 32, fontWeight: 900,
                          color: counts[ty.id] ? colorOf(ty) : 'var(--mid)',
                        }}>{counts[ty.id] || 0}</div>
                        <button onClick={() => change(ty.id, -1)} disabled={busy || !counts[ty.id]}
                          className="touch-btn"
                          style={{
                            width: 62, height: 72, background: 'transparent',
                            border: '2px solid var(--border)', borderRadius: 14,
                            color: counts[ty.id] ? 'var(--text)' : 'var(--dim)',
                            fontSize: 26, fontWeight: 800,
                            cursor: counts[ty.id] ? 'pointer' : 'not-allowed',
                          }}>−1</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div style={{ color: 'var(--dim)', fontSize: 14 }}>
            {t('Ошиблись кнопкой — нажмите «−1». Пока задание не завершено, это можно исправить.')}
          </div>
        </div>
      )}
    </>
  );
}

// ── Остановка пресса ────────────────────────────────────────────────────────
//
// Пресс стоял сорок минут — почему, не знал никто. Меняли матрицу, чинили,
// обедали, ждали плиту: в цифрах это выглядело одинаково, а по смыслу
// разное — одно неизбежно, второе планируется, третье теряется зря.
//
// Причины приходят из справочника: цех у каждого свой, и список, который
// нельзя поправить, заполняется «Другим» и перестаёт что-либо объяснять.

const hhmmss = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), q = s % 60;
  const p = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${p(m)}:${p(q)}` : `${p(m)}:${p(q)}`;
};

function StopPanel({ press, onStopChange, lastActivity, resumeRef }) {
  const [stop, setStop]       = useState(null);
  const [reasons, setReasons] = useState([]);
  const [idleMin, setIdleMin] = useState(0);
  const [pick, setPick]       = useState(false);
  const [busy, setBusy]       = useState(false);
  const [now,  setNow]        = useState(() => Date.now());
  // Отсчёт ведём от секунд СЕРВЕРА, а не от времени моноблока: часы у
  // терминала в цеху сбиты почти всегда, и «остановлен 3 часа назад» при
  // остановке пять минут назад оператор читает как поломку программы.
  const [since, setSince] = useState(null);
  const sending = useRef(false);

  const load = useCallback(() => {
    productionAPI.getPress(press.id)
      .then(r => {
        setStop(r.data.active_stop || null);
        setIdleMin(Number(r.data.idle_minutes) || 0);
      })
      .catch(() => {});
  }, [press.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const tm = setInterval(load, 20000);
    return () => clearInterval(tm);
  }, [load]);
  useEffect(() => {
    productionAPI.getStopReasons({ pickable: 'true' })
      .then(r => setReasons(r.data.results || r.data))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!stop) return undefined;
    const tm = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tm);
  }, [stop]);
  useEffect(() => {
    setSince(stop ? Date.now() - (stop.seconds || 0) * 1000 : null);
  }, [stop]);
  useEffect(() => { if (onStopChange) onStopChange(stop); }, [stop, onStopChange]);

  const send = async (data) => {
    if (sending.current) return null;
    sending.current = true;
    setBusy(true);
    try {
      const { data: out } = await productionAPI.stopPress(press.id, data);
      return out;
    } catch (err) {
      // Сервер мог отметить простой раньше нас — тогда это не ошибка,
      // а та же остановка, и её надо просто показать.
      load();
      if (err.response?.status !== 400) toast.error(err.response?.data?.error || 'Ошибка');
      return null;
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };

  const doStop = async (reason) => {
    const out = await send({ reason: reason.id });
    if (!out) return;
    setPick(false);
    setStop({ id: out.stop_id, reason: out.reason, reason_display: out.reason_display,
              is_auto: false, blocks_work: out.blocks_work !== false, seconds: 0 });
    toast('⏸ ' + t('Пресс остановлен') + ' · ' + t(out.reason_display));
  };

  // Система отметила простой, а оператор говорит, из-за чего он был. Это не
  // вторая остановка — та же, объяснённая: время не теряется.
  const explain = async (reason) => {
    const out = await send({ reason: reason.id });
    if (!out) return;
    setStop(prev => ({ ...(prev || {}), reason: out.reason,
                       reason_display: out.reason_display, is_auto: false,
                       blocks_work: out.blocks_work !== false }));
    toast.success(t('Причина записана') + ': ' + t(out.reason_display));
  };

  const autoStop = useCallback(async () => {
    if (sending.current) return;
    sending.current = true;
    try {
      const { data: out } = await productionAPI.stopPress(press.id, { auto: true });
      setStop({ id: out.stop_id, reason: out.reason, reason_display: out.reason_display,
                is_auto: true, blocks_work: true, seconds: 0 });
    } catch {
      load();
    } finally { sending.current = false; }
  }, [press.id, load]);

  // Пресс молчит дольше нормы — отмечаем простой, не дожидаясь оператора.
  // То же самое делает сервер (окно у пресса могут и закрыть), но здесь это
  // видно сразу: экран закрывается в ту же секунду, когда время пошло.
  useEffect(() => {
    if (stop || !idleMin || !lastActivity) return undefined;
    const tm = setInterval(() => {
      if (Date.now() - lastActivity >= idleMin * 60000) autoStop();
    }, 5000);
    return () => clearInterval(tm);
  }, [stop, idleMin, lastActivity, autoStop]);

  const doResume = async () => {
    setBusy(true);
    try {
      const { data } = await productionAPI.resumePress(press.id);
      setStop(null);
      if (data.stopped) {
        toast.success('▶ ' + t('Работа продолжена') + ' · ' +
                      t(data.reason_display) + ': ' + hhmmss(data.seconds || 0));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
      load();
    } finally { setBusy(false); }
  };

  // Нажатие «+1» тоже снимает простой, а кнопка «Продолжить» живёт здесь —
  // отдаём её наружу, чтобы экран не ждал следующего опроса сервера.
  useEffect(() => {
    if (resumeRef) resumeRef.current = doResume;
  });

  const elapsed = since != null ? (now - since) / 1000 : 0;

  const tile = (r, onClick) => (
    <button key={r.id} onClick={() => onClick(r)} disabled={busy}
      className="touch-btn" style={{
        background: 'var(--bg)', border: '2px solid var(--border)',
        borderRadius: 14, padding: '18px 14px', cursor: 'pointer',
        color: 'var(--text)', fontSize: 17, fontWeight: 700,
        fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 12,
        textAlign: 'left',
      }}>
      <span style={{ fontSize: 26 }}>{r.icon || '⏸'}</span>{t(r.name)}
    </button>
  );

  // ── Настройка режима: время идёт, кнопки живые ──────────────────────────
  //
  // Режим на новом заказе подбирают прессованием: жмут лист, смотрят, что
  // вышло, правят температуру. Закрыть экран здесь значит отнять инструмент.
  if (stop && stop.blocks_work === false) {
    return (
      <div style={{
        position: 'fixed', top: 70, left: '50%', transform: 'translateX(-50%)',
        zIndex: 3500, background: '#f59e0b', color: '#111',
        borderRadius: 14, padding: '10px 16px', display: 'flex',
        alignItems: 'center', gap: 14, boxShadow: '0 6px 20px rgba(0,0,0,.3)',
      }}>
        <span style={{ fontSize: 22 }}>🎛</span>
        <span style={{ fontSize: 16, fontWeight: 800 }}>{t(stop.reason_display)}</span>
        <span style={{
          fontSize: 22, fontWeight: 900, fontVariantNumeric: 'tabular-nums',
        }}>{hhmmss(elapsed)}</span>
        <button onClick={doResume} disabled={busy} className="touch-btn" style={{
          background: '#111', color: '#fff', border: 'none', borderRadius: 10,
          padding: '10px 20px', fontSize: 15, fontWeight: 800, cursor: 'pointer',
          fontFamily: 'inherit',
        }}>✓ {t('Режим настроен')}</button>
      </div>
    );
  }

  // ── Обычная остановка: экран закрыт целиком ─────────────────────────────
  //
  // Полосой поверх работы это не работало: кнопки под ней оставались живыми,
  // оператор жал «+1», получал предупреждение, жал ещё — и предупреждения
  // копились стопкой. Станок стоит — значит и терминал стоит.
  if (stop) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 4000,
        background: 'rgba(127,17,17,.97)', color: '#fff',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: 24, textAlign: 'center', overflowY: 'auto',
      }}>
        <div style={{ fontSize: 64, lineHeight: 1 }}>⏸</div>
        <div style={{ fontSize: 34, fontWeight: 900, letterSpacing: .5, marginTop: 12 }}>
          {t('ПРЕСС ОСТАНОВЛЕН')}
        </div>
        <div style={{
          marginTop: 10, background: 'rgba(255,255,255,.16)', borderRadius: 30,
          padding: '8px 26px', fontSize: 23, fontWeight: 800,
        }}>{t(stop.reason_display)}</div>

        <div style={{
          fontSize: 76, fontWeight: 900, fontVariantNumeric: 'tabular-nums',
          margin: '20px 0 2px', lineHeight: 1,
        }}>{hhmmss(elapsed)}</div>
        <div style={{ fontSize: 16, opacity: .85 }}>{t('время записывается')}</div>

        {/* Простой отметила система. Оператор вернулся — спрашиваем, из-за
            чего он был: иначе это время так и останется необъяснённым. */}
        {stop.is_auto && reasons.length > 0 && (
          <div style={{ marginTop: 24, width: '100%', maxWidth: 620 }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              {t('Из-за чего стояли?')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {reasons.map(r => (
                <button key={r.id} onClick={() => explain(r)} disabled={busy}
                  className="touch-btn" style={{
                    background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.3)',
                    borderRadius: 12, padding: '13px 12px', cursor: 'pointer',
                    color: '#fff', fontSize: 15.5, fontWeight: 700,
                    fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 10,
                    textAlign: 'left',
                  }}>
                  <span style={{ fontSize: 21 }}>{r.icon || '⏸'}</span>{t(r.name)}
                </button>
              ))}
            </div>
          </div>
        )}

        <button onClick={doResume} disabled={busy} className="touch-btn" style={{
          background: '#fff', color: '#b91c1c', border: 'none', borderRadius: 18,
          padding: '24px 56px', fontSize: 28, fontWeight: 900, cursor: 'pointer',
          fontFamily: 'inherit', marginTop: 32, minWidth: 340,
          boxShadow: '0 8px 26px rgba(0,0,0,.3)',
        }}>▶ {t('Продолжить работу')}</button>

        <div style={{ marginTop: 18, fontSize: 15, opacity: .8, maxWidth: 460 }}>
          {t('Пока пресс остановлен, листы не считаются')}
        </div>
      </div>
    );
  }

  // Своей полосы у кнопки нет: она занимала строку во всю ширину экрана ради
  // одной кнопки, а у пресса экран и так тесный. Место ей — в верхнем ряду.
  return (
    <>
      <button onClick={() => setPick(true)} className="touch-btn" style={{
        background: 'var(--bg)', border: '1px solid var(--border)',
        borderRadius: 8, color: 'var(--muted)', padding: '7px 14px',
        fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
        whiteSpace: 'nowrap',
      }}>⏸ {t('Остановка')}</button>

      {pick && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.72)', zIndex: 3000,
          display: 'grid', placeItems: 'center', padding: 20,
        }} onClick={() => !busy && setPick(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--card)', borderRadius: 18, padding: 24,
            width: '100%', maxWidth: 640, border: '1px solid var(--border)',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ fontSize: 21, fontWeight: 800, marginBottom: 4 }}>
              {t('Почему пресс встал?')}
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 18 }}>
              {t('Время пойдёт с этой секунды и до кнопки «Продолжить»')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {reasons.map(r => tile(r, doStop))}
            </div>
            {reasons.length === 0 && (
              <div style={{ color: 'var(--dim)', fontSize: 15 }}>
                {t('Список причин пуст — его заполняет администратор')}
              </div>
            )}
            <button onClick={() => setPick(false)} disabled={busy} className="touch-btn" style={{
              width: '100%', marginTop: 14, background: 'none',
              border: '1px solid var(--border)', borderRadius: 12,
              color: 'var(--dim)', padding: '13px 0', fontSize: 16,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>{t('Отмена')}</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Замена плиты или плёнки посреди работы ──────────────────────────────────
//
// Кончилась плёнка, привезли плиту другого завода — заказ тот же, а материал
// другой. Раньше для этого надо было завершить смену и начать заново, и никто
// этого не делал: правда о материале оставалась утренней, а разбор брака по
// заводу врал.
//
// Окно открывается заполненным тем, что стоит у пресса сейчас. Меняют одно
// поле, оба или закрывают, ничего не тронув. С момента сохранения материал
// получает каждый следующий лист; выпущенные до замены остаются со своим.
function MaterialModal({ item, onClose, onSaved }) {
  const [cur, setCur] = useState(null);
  const [makers, setMakers] = useState([]);
  const [saving, setSaving] = useState(false);

  const INP = {
    width: '100%', padding: '13px 14px', background: 'var(--bg)',
    border: '2px solid var(--border)', borderRadius: 12, color: 'var(--text)',
    fontSize: 18, boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const LBL = { display: 'block', color: 'var(--muted)', fontSize: 14,
                marginBottom: 6, fontWeight: 600 };

  // Открыли окно — пресс стоит: паллету снимают, плёнку заправляют. Сервер
  // отмечает остановку «Замена материала» и отдаёт текущие значения.
  useEffect(() => {
    productionAPI.material(item.id, { action: 'open' })
      .then(r => setCur(r.data))
      .catch(e => { toast.error(e.response?.data?.error || 'Ошибка'); onClose(); });
    warehouseAPI.getMaterialMakers()
      .then(r => setMakers(r.data.results || r.data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const set = (k, v) => setCur(prev => ({ ...prev, [k]: v }));
  const forKind = (kind) => makers.filter(m => m.kind === kind || m.kind === 'both');

  const cancel = () => {
    productionAPI.material(item.id, { action: 'cancel' }).catch(() => {});
    onClose();
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await productionAPI.material(item.id, {
        action: 'save',
        dsp_maker: cur.dsp_maker || '',
        dsp_batch: cur.dsp_batch || '',
        film_maker: cur.film_maker || '',
        film_pallet: cur.film_pallet || '',
      });
      toast.success(t('Материал заменён — идёт в новые листы'));
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Ошибка');
    } finally { setSaving(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000000cc', zIndex: 320,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
    }}>
      <div style={{
        background: 'var(--card)', borderRadius: 20, padding: 22,
        width: '100%', maxWidth: 620, maxHeight: '92vh', overflowY: 'auto',
      }}>
        <h2 style={{ color: 'var(--text)', fontSize: 20, fontWeight: 800, margin: '0 0 4px' }}>
          🔄 {t('Замена материала')}
        </h2>
        <p style={{ color: 'var(--muted)', fontSize: 14, margin: '0 0 16px' }}>
          Меняйте только то, что заменили. Новые листы пойдут с этими
          значениями, прежние останутся со своими. Пока окно открыто, у пресса
          отмечена остановка «Замена материала».
        </p>

        {!cur ? (
          <p style={{ color: 'var(--dim)', textAlign: 'center', padding: 24 }}>{t('Загрузка…')}</p>
        ) : (
          <>
            <div style={{ display: 'grid', gap: 14, marginBottom: 18 }}>
              <div>
                <label style={LBL}>🏭 {t('Завод плиты')}</label>
                <select value={cur.dsp_maker || ''} style={{ ...INP, cursor: 'pointer' }}
                  onChange={e => set('dsp_maker', e.target.value)}>
                  <option value="">{t('— не указан —')}</option>
                  {forKind('dsp').map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <label style={LBL}>🏷 {t('Партия плиты')}</label>
                <input type="text" value={cur.dsp_batch || ''} style={INP}
                  autoComplete="off" placeholder={t('номер или дата')}
                  onChange={e => set('dsp_batch', e.target.value)} />
              </div>
              <div>
                <label style={LBL}>🏭 {t('Завод плёнки')}</label>
                <select value={cur.film_maker || ''} style={{ ...INP, cursor: 'pointer' }}
                  onChange={e => set('film_maker', e.target.value)}>
                  <option value="">{t('— не указан —')}</option>
                  {forKind('film').map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <label style={LBL}>🎞 {t('Паллета плёнки')}</label>
                <input type="text" value={cur.film_pallet || ''} style={INP}
                  autoComplete="off" placeholder={t('номер')}
                  onChange={e => set('film_pallet', e.target.value)} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={save} disabled={saving} className="touch-btn" style={{
                flex: 2, height: 58, background: 'var(--accent)', border: 'none',
                borderRadius: 14, color: '#fff', fontSize: 17, fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              }}>{saving ? '…' : t('✅ Сохранить и продолжить')}</button>
              <button onClick={cancel} className="touch-btn" style={{
                flex: 1, height: 58, background: 'var(--bg)', color: 'var(--muted)',
                border: '1px solid var(--border)', borderRadius: 14, fontSize: 16,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>{t('Закрыть без замены')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ActiveJobScreen({ item, press, worker, onBack, onCompleted, onSwitched }) {
  const [done, setDone]       = useState(item.quantity_done || 0);
  // Пачка, в которой идёт эта работа: заказы с тем же ЛДСП в эту смену.
  // Нужна, чтобы оператор видел не «3 из 10», а сколько всего осталось
  // пресовать подряд и какой заказ пойдёт следом.
  const [pack, setPack] = useState([]);
  useEffect(() => {
    if (!item.group_key) { setPack([]); return; }
    productionAPI.getQueue({ press_id: press.id })
      .then(r => setPack((r.data || []).filter(x =>
        x.group_key === item.group_key && x.status !== 'done').sort(byWorkOrder)))
      .catch(() => {});
  }, [item.id, item.group_key, press.id]);
  const [total]               = useState(item.quantity);
  // Сводка по сортам из панели справа: план считается по высшему сорту, и та
  // же сводка показывается в окне завершения
  const [stats, setStats] = useState(null);
  const [pressing, setPressing]             = useState(false);
  const [stopping, setStopping]             = useState(false);
  // Пресс стоит — «+1» не считаем. Иначе замена матрицы, отмеченная кнопкой,
  // и листы, «выпущенные» во время неё, противоречат друг другу, а разобрать
  // потом, что было на самом деле, уже нечем.
  const [stopped, setStopped]               = useState(null);
  // Когда шёл последний лист. От него считается молчание пресса: не нажали
  // дольше нормы — простой отмечается сам, спрашивать некого.
  const [lastPress, setLastPress]           = useState(() => Date.now());
  const resumeRef                           = useRef(null);
  const [flash, setFlash]                   = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showMaterial, setShowMaterial] = useState(false);
  const [cfg, setCfg]           = useState(() => loadPrintCfg(press.id));
  const [showPrintCfg, setShowPrintCfg] = useState(false);
  const [lastSheetId, setLastSheetId]   = useState(null);
  // Бирка созламаси — ШУ БРАУЗЕРда (localStorage), ҳар пресс учун алоҳида
  const autoPrint = cfg.auto;
  const copies    = cfg.copies;

  // Созлама ўзгарса — ўша пресс учун сақланади (бир марта босилса — доимий)
  const updateCfg = useCallback((patch) => {
    setCfg(prev => {
      const next = { ...prev, ...patch };
      savePrintCfg(press.id, next);
      return next;
    });
  }, [press.id]);

  // Простой сняли — молчание считаем заново. Без этого условие «прошло больше
  // двух минут» оставалось верным навсегда: оператор жал «Продолжить», и через
  // пять секунд экран снова закрывался. Так и было — по журналу видно.
  useEffect(() => {
    if (!stopped) setLastPress(Date.now());
  }, [stopped]);

  const toggleAutoPrint = () => {
    const next = !autoPrint;
    updateCfg({ auto: next });
    toast(next ? t('🖨 Автопечать ВКЛЮЧЕНА — бирка на каждый лист выходит сама')
               : t('🖨 Автопечать ВЫКЛЮЧЕНА'));
  };

  // План закрывается ВЫСШИМ сортом, а не всем, что вышло из пресса. При 32
  // выпущенных и 4 пересортированных экран кричал «перевыполнение на 2», хотя
  // годных было 28 из 30 и работать оставалось. Пока панель сортов не ответила,
  // считаем по выпуску — иначе первые секунды экран показывал бы ноль.
  const good      = stats ? stats.top : done;
  const overPlan  = good > total;
  const remaining = Math.max(0, total - good);
  const progress  = total > 0 ? Math.min((good / total) * 100, 100) : 0;

  const barColor  = overPlan ? '#ef4444' : remaining <= Math.ceil(total * 0.1) ? '#f59e0b' : 'var(--accent)';
  const btnColor  = overPlan
    ? (flash ? '#b91c1c' : '#dc2626')
    : (flash ? '#16a34a' : pressing ? '#15803d' : '#22c55e');

  const handleIncrement = useCallback(async () => {
    if (pressing) return;
    // Настройка режима идёт прессованием — там «+1» как раз и нужен.
    if (stopped && stopped.blocks_work !== false) {
      // Простой отметила система, а человек у пресса — вот он, жмёт. Само
      // нажатие и есть ответ «работаю»: снимаем простой и считаем лист.
      // Остановку, которую поставил человек (ремонт, обед), нажатием не
      // снимаем — её снимает тот же человек, осознанно.
      if (!stopped.is_auto) {
        // Один и тот же id: пробел под модалом можно удерживать, и без него
        // предупреждения выстраивались стопкой во весь экран.
        toast(t('Пресс остановлен') + ' — ' + t('нажмите «Продолжить работу»'),
              { id: 'press-stopped' });
        return;
      }
      try { if (resumeRef.current) await resumeRef.current(); } catch (e) {}
    }
    setPressing(true);
    setFlash(true);
    setTimeout(() => setFlash(false), 150);
    try {
      const { data } = await productionAPI.incrementJob(item.id);
      setDone(data.quantity_done);
      setLastPress(Date.now());
      if (data.switched && onSwitched) {
        // Прежний заказ закрыт сервером, лист лёг в следующий. Экран
        // переходит сам: у нового заказа свой номер, заказчик и план.
        const next = pack.find(x => x.id === data.queue_item);
        toast.success(`Заказ #${item.order_number} выполнен — дальше`
          + (next ? ` #${next.order_number}` : ' следующий заказ'),
          { id: 'pack-switch' });
        onSwitched(data.queue_item);
      }
      if (data.sheet_id) {
        setLastSheetId(data.sheet_id);
        // Бирка ЯШИРИН iframe орқали чиқади — янги ойна ОЧИЛМАЙДИ. Chrome
        // --kiosk-printing билан ишга туширилса «Печать» диалоги ҳам чиқмайди.
        if (autoPrint) {
          printSheetLabelBySize(data.sheet_id, cfg.size, { copies: cfg.copies });
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally {
      setPressing(false);
    }
  }, [pressing, stopped, item.id, item.order_number, autoPrint, cfg.size,
      cfg.copies, pack, onSwitched]);


  // Промах у пресса виден сразу, в ту же секунду. Раньше исправить его можно
  // было только со склада и только пока смену не забрали — а оператор всё это
  // время знал и молчал.
  const handleDecrement = useCallback(async () => {
    if (pressing || done <= 0) return;
    const ok = await confirmToast(
      'Снять один лист? Счётчик станет ' + (done - 1) + '.\n'
      + 'Напечатанную бирку вернуть нельзя — выбросьте её.',
      { title: 'Лишнее нажатие', confirmText: 'Да, снять лист',
        danger: true, big: true });
    if (!ok) return;
    setPressing(true);
    try {
      const { data } = await productionAPI.decrementJob(item.id);
      setDone(data.quantity_done);
      setLastSheetId(null);        // бирки этого листа больше нет
      toast.success('Лист снят' + (data.removed_code ? ' · ' + data.removed_code : ''));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally {
      setPressing(false);
    }
  }, [pressing, done, item.id]);

  const reprintLast = useCallback(() => {
    if (!lastSheetId) { toast(t("Бирки ещё нет — сначала нажмите «+1 ГОТОВО»")); return; }
    // Авто-печат ЎЧИРИЛГАН бўлса — оператор ўзи чоп этмоқчи: печат ойнасини очамиз
    if (!autoPrint) {
      printSheetLabelBySize(lastSheetId, cfg.size, { newWindow: true });
      return;
    }
    printSheetLabelBySize(lastSheetId, cfg.size, { copies: cfg.copies });
    toast(t("🖨 Бирка отправлена на принтер"));
  }, [lastSheetId, autoPrint, cfg.size, cfg.copies]);

  const handleConfirmComplete = useCallback(async (payload) => {
    setStopping(true);
    try {
      await productionAPI.completeJob(item.id, payload);
      toast.success(overPlan
        ? `✅ Завершено: ${done} шт (план: ${total})`
        : '✅ Задание завершено'
      );
      onCompleted();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message || 'Ошибка завершения';
      toast.error(msg);
    } finally {
      setStopping(false);
      setShowCompleteModal(false);
    }
  }, [done, total, overPlan, item.id, onCompleted]);

  useEffect(() => {
    const handler = (e) => {
      // В поле ввода эти клавиши значат своё — там не перехватываем
      const tag = ((e.target && e.target.tagName) || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      // Tab здесь наравне с пробелом: прессовщики жмут его по привычке — им
      // он и означает «лист пошёл». Раз жмут, значит работают, значит простоя
      // нет — и лист надо посчитать, а не потерять.
      if (e.code === 'Space' || e.code === 'Enter' || e.code === 'Tab') {
        e.preventDefault();
        handleIncrement();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleIncrement]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      {/* Шапка */}
      <div style={{
        background: 'var(--card)', borderBottom: '1px solid var(--border)',
        padding: '0 24px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 18 }}>⚙️</span>
          <span style={{ color: 'var(--text)', fontWeight: 700, fontSize: 17 }}>{press.name}</span>
          {/* Имя того, чей ПИН ввели: на него пишется каждый лист и каждый
              брак. Чужой ПИН иначе всплывёт только в отчёте за месяц. */}
          {worker && (
            <span style={{
              background: 'var(--accent-weak)', color: 'var(--accent)',
              borderRadius: 20, padding: '4px 12px', fontSize: 14, fontWeight: 700,
              whiteSpace: 'nowrap',
            }}>👤 {worker.name}</span>
          )}
          {press.current_texture_name && (
            <span style={{
              background: 'var(--bg)', borderRadius: 6,
              padding: '3px 10px', color: 'var(--muted)', fontSize: 13
            }}>{press.current_texture_name}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <StopPanel press={press} onStopChange={setStopped}
            lastActivity={lastPress} resumeRef={resumeRef} />
          <button onClick={toggleAutoPrint} title={t("Печатать бирку на каждый лист автоматически")}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: autoPrint ? '#22c55e18' : 'var(--bg)',
              border: '1px solid ' + (autoPrint ? '#22c55e60' : 'var(--border)'),
              borderRadius: 20, padding: '6px 14px', cursor: 'pointer',
              color: autoPrint ? '#22c55e' : 'var(--dim)', fontSize: 13, fontWeight: 700,
            }}>
            <span style={{
              width: 32, height: 18, borderRadius: 10, position: 'relative',
              background: autoPrint ? '#22c55e' : 'var(--border)', transition: 'background .15s',
            }}>
              <span style={{
                position: 'absolute', top: 2, left: autoPrint ? 16 : 2,
                width: 14, height: 14, borderRadius: '50%', background: '#fff',
                transition: 'left .15s',
              }} />
            </span>
            🖨 Бирка {autoPrint ? 'ВКЛ' : 'ВЫКЛ'}
          </button>
          <button onClick={() => setShowPrintCfg(true)} title={t("Настройки принтера и бирки")}
            style={{
              background: 'var(--bg)', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--muted)', padding: '7px 12px',
              cursor: 'pointer', fontSize: 14
            }}>⚙️ Принтер</button>
          <FullscreenBtn />
          <ThemeBtn />
          <LangSwitch compact />
          <Clock />
          <button onClick={onBack} style={{
            background: 'none', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--dim)', padding: '7px 14px',
            cursor: 'pointer', fontSize: 13
          }}>{t('← Назад')}</button>
        </div>
      </div>

      {/* Модал: принтер / бирка созламалари */}
      {showPrintCfg && (
        <PrintSettingsModal
          cfg={cfg} pressName={press.name}
          onChange={updateCfg}
          onClose={() => setShowPrintCfg(false)}
        />
      )}

      {/* Модал: ишни якунлаш */}
      {showMaterial && (
        <MaterialModal
          item={item}
          onClose={() => setShowMaterial(false)}
          onSaved={() => { setShowMaterial(false); setLastPress(Date.now()); }}
        />
      )}

      {showCompleteModal && (
        <CompleteJobModal
          stopping={stopping}
          done={done} plan={total} good={good} stats={stats}
          onConfirm={handleConfirmComplete}
          onCancel={() => setShowCompleteModal(false)}
        />
      )}

      {/* Контент — три колонки: кнопки | счётчик | брак.
          Всё, что нужно оператору, видно сразу: ни вертикального, ни
          горизонтального скролла на сменном экране быть не должно. */}
      <div className="terminal-grid">
        {/* ── Слева: кнопки ─────────────────────────────────────────── */}
        <div className="terminal-col-actions">
          <button
            onClick={handleIncrement}
            disabled={pressing}
            className="touch-btn mob-btn-plus"
            style={{
              height: 150, background: btnColor,
              border: 'none', borderRadius: 24,
              color: '#fff', fontSize: 40, fontWeight: 900,
              cursor: pressing ? 'not-allowed' : 'pointer',
              transition: 'background 0.1s, transform 0.1s',
              transform: pressing ? 'scale(0.97)' : 'scale(1)',
              boxShadow: overPlan
                ? '0 8px 32px rgba(239,68,68,0.35)'
                : '0 8px 32px rgba(34,197,94,0.35)',
              letterSpacing: 2, userSelect: 'none', WebkitUserSelect: 'none',
            }}
          >{pressing ? '...' : '+ 1  ГОТОВО'}</button>

          {/* «−1» — рядом, но нарочно маленькая и блёклая: жать её надо редко
              и осознанно, а промахнуться по ней в перчатке нельзя.

              На нуле она гаснет, но НЕ исчезает: кнопка, которая то есть, то
              нет, заставляет искать её глазами каждый раз. Место у неё одно и
              то же — это и есть половина дела у станка. */}
          <button
            onClick={handleDecrement}
            disabled={pressing || done <= 0}
            className="touch-btn"
            style={{
              height: 54, background: 'var(--card)',
              border: '2px solid var(--border)', borderRadius: 14,
              color: done > 0 ? 'var(--muted)' : 'var(--dim)',
              fontSize: 17, fontWeight: 700,
              opacity: done > 0 ? 1 : 0.45,
              cursor: (pressing || done <= 0) ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >− 1 · {t('лишний лист')}</button>

          {/* Выйти — частое действие, поэтому широкая.
              Завершить — необратимое и раз в смену, поэтому компактная. */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onBack}
              className="touch-btn"
              style={{
                flex: 1, height: 60,
                background: 'var(--accent)',
                border: 'none', borderRadius: 16,
                color: '#fff', fontSize: 17, fontWeight: 700,
                cursor: 'pointer',
              }}
            >{t('🚪 Выйти')}</button>

            <button
              onClick={() => setShowCompleteModal(true)}
              disabled={stopping}
              className="touch-btn mob-btn-stop"
              style={{
                height: 60, padding: '0 18px',
                background: 'transparent',
                border: '2px solid #dc2626', borderRadius: 16,
                color: '#dc2626', fontSize: 15, fontWeight: 700,
                cursor: stopping ? 'not-allowed' : 'pointer',
                opacity: stopping ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >{stopping ? 'Завершение...' : '⏹ Завершить'}</button>
          </div>

          {/* Замена плиты или плёнки посреди заказа. Раньше для этого надо
              было завершить смену и начать заново — этого никто не делал, и
              материал в базе оставался утренним. */}
          <button
            onClick={() => setShowMaterial(true)}
            className="touch-btn"
            style={{
              height: 54, background: 'var(--card)',
              border: '2px solid var(--border)', borderRadius: 14,
              color: 'var(--muted)', fontSize: 16, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >🔄 {t('Заменить плиту / плёнку')}</button>

          {/* Қўлда чоп этиш — автопечать выключена ёки бирка чиқмай қолган ҳолат учун.
              Раньше кнопка была пунктиром цвета рамки — в тёмной теме её
              просто не было видно. Теперь это обычная карточка. */}
          <button
            onClick={reprintLast}
            disabled={!lastSheetId}
            className="touch-btn"
            style={{
              height: 52, background: lastSheetId ? 'var(--card)' : 'transparent',
              border: '1px solid ' + (lastSheetId ? 'var(--mid)' : 'var(--border)'),
              borderRadius: 12,
              color: lastSheetId ? 'var(--text)' : 'var(--dim)',
              fontSize: 14, fontWeight: 700,
              cursor: lastSheetId ? 'pointer' : 'not-allowed',
              opacity: lastSheetId ? 1 : 0.6,
            }}
          >{autoPrint ? t('🖨 Напечатать последнюю бирку заново') : t('🖨 Печать бирки (откроется окно)')}</button>

          <div style={{ color: 'var(--dim)', fontSize: 12, textAlign: 'center', lineHeight: 1.4 }}>
            {t('Нажмите кнопку, пробел или Enter после каждого листа')}
            {autoPrint
              ? ` · ${copies > 1 ? copies + ' ' : ''}${t('бирка печатается сама')}`
              : ' · ' + t('автопечать выключена')}
          </div>
        </div>

        {/* ── В центре: что делаем и сколько сделали ────────────────── */}
        <div className="terminal-col-main">
        {/* Инфо */}
        <div style={{
          background: 'var(--card)', borderRadius: 16,
          padding: '14px 20px', textAlign: 'center',
          width: '100%'
        }}>
          <div style={{ color: 'var(--text)', fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
            {item.product_name}
          </div>
          {item.is_service && (
            <div style={{ marginBottom: 6 }}><ServiceBadge big /></div>
          )}
          {pack.length > 1 && (() => {
            const plan = pack.reduce((n, x) => n + (x.quantity || 0), 0);
            const made = pack.reduce((n, x) =>
              n + (x.id === item.id ? done : (x.quantity_done || 0)), 0);
            const rest = pack.filter(x => x.id !== item.id);
            return (
              <div style={{
                margin: '4px auto 8px', display: 'inline-block',
                background: '#f59e0b18', border: '1px solid #f59e0b55',
                borderRadius: 10, padding: '6px 14px', fontSize: 15,
              }}>
                <b style={{ color: '#f59e0b' }}>Пачка: {made} / {plan} шт</b>
                <span style={{ color: 'var(--muted)', marginLeft: 8 }}>
                  следом: {rest.map(x => '#' + x.order_number).join(', ')}
                </span>
              </div>
            );
          })()}
          <div style={{ color: 'var(--muted)', fontSize: 15 }}>
            {item.texture_name}
            <span style={{ color: 'var(--border)', margin: '0 10px' }}>·</span>
            Заказ <span style={{ color: '#f59e0b', fontWeight: 700 }}>#{item.order_number}</span>
            <span style={{ color: 'var(--border)', margin: '0 10px' }}>·</span>
            {item.client_name}
          </div>
        </div>

        {/* Счётчик */}
        <div style={{
          background: 'var(--card)', borderRadius: 24,
          padding: '22px 24px', textAlign: 'center',
          // ширину задаёт колонка: у карточки со счётчиком стоял свой
          // max-width, и она была уже карточки с наименованием
          width: '100%',
          border: `2px solid ${barColor}30`
        }}>
          <div style={{ marginBottom: 6 }}>
            <span className="mob-counter-big" style={{
              fontSize: 84, fontWeight: 900, lineHeight: 1,
              color: overPlan ? '#ef4444' : 'var(--text)',
              transition: 'color 0.2s'
            }}>{done}</span>
            <span className="mob-counter-slash" style={{ color: 'var(--border)', fontSize: 40, fontWeight: 300, margin: '0 8px' }}>/</span>
            <span className="mob-counter-plan" style={{ fontSize: 46, fontWeight: 700, color: 'var(--mid)' }}>{total}</span>
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 14, marginBottom: 12 }}>{t('листов готово')}</div>

          <div style={{ background: 'var(--bg)', borderRadius: 10, height: 12, marginBottom: 12 }}>
            <div style={{
              width: `${progress}%`, height: 12,
              background: barColor, borderRadius: 10,
              transition: 'width 0.25s ease'
            }} />
          </div>

          {overPlan ? (
            <div style={{
              background: '#ef444418', border: '1px solid #ef444430',
              borderRadius: 10, padding: '10px 20px',
              color: '#ef4444', fontSize: 16, fontWeight: 700
            }}>
              ⚠ Перевыполнение плана на {good - total} шт
            </div>
          ) : remaining === 0 ? (
            <div style={{ fontSize: 15, color: '#22c55e', fontWeight: 600 }}>
              ✅ План выполнен — нажмите «Завершить» или продолжайте
            </div>
          ) : remaining <= 5 ? (
            <div style={{ fontSize: 18, color: '#f59e0b' }}>
              Осталось: <b>{remaining} шт</b>
            </div>
          ) : (
            <div style={{ fontSize: 18, color: 'var(--dim)' }}>
              Осталось: <b style={{ color: 'var(--muted)' }}>{remaining} шт</b>
            </div>
          )}
        </div>
        </div>

        {/* ── Справа: брак ──────────────────────────────────────────── */}
        {/* done в ключе: выпустили лист — предел разбраковки вырос, панель
            должна это увидеть сразу, а не после перезахода на экран */}
        <DefectSection queueItemId={item.id} version={done} planned={total}
          onStats={setStats} />
      </div>
    </div>
  );
}

function PrintSettingsModal({ cfg, pressName, onChange, onClose }) {
  const [testing, setTesting] = useState(false);

  const runTest = async () => {
    setTesting(true);
    try {
      await printTestLabel(cfg);
      toast(t("🖨 Пробная бирка отправлена (через браузер)"));
    } catch (e) {
      toast.error('Пробная печать не прошла');
    } finally { setTesting(false); }
  };

  const Row = ({ title, hint, children }) => (
    <div style={{ marginBottom: 18 }}>
      <div style={{ color: 'var(--text)', fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{title}</div>
      {hint && <div style={{ color: 'var(--dim)', fontSize: 12, marginBottom: 8 }}>{hint}</div>}
      {children}
    </div>
  );
  const pill = (active) => ({
    flex: 1, padding: '12px 10px', borderRadius: 12, cursor: 'pointer',
    border: '2px solid ' + (active ? '#22c55e' : 'var(--border)'),
    background: active ? '#22c55e18' : 'var(--bg)',
    color: active ? '#22c55e' : 'var(--muted)',
    fontSize: 15, fontWeight: 700,
  });

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--card)', borderRadius: 20, padding: 24,
        width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ color: 'var(--text)', fontSize: 20, fontWeight: 800, marginBottom: 4 }}>
          🖨 {t('Настройки принтера')}
        </div>
        <div style={{ color: 'var(--dim)', fontSize: 13, marginBottom: 16 }}>
          {pressName}
        </div>

        {/* Диалогсиз чоп этиш — Chrome созламаси */}
        <div style={{
          background: '#f59e0b14', border: '1px solid #f59e0b40',
          borderRadius: 12, padding: '12px 14px', marginBottom: 18,
          color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.5,
        }}>
          <div style={{ color: '#f59e0b', fontWeight: 800, marginBottom: 6 }}>
            {t('чтобы не появлялось окно «Печать»')}
          </div>
          {t('1. Ярлык Chrome → Свойства → Объект: в конце дописать')} <code>--kiosk-printing</code><br />
          {t('2. Закрыть Chrome ПОЛНОСТЬЮ и открыть только этим ярлыком')}<br />
          {t('3. Сделать принтер бирок основным в Windows')}
        </div>

        <Row title={t("Автопечать")}
             hint={t("Если включено — бирка печатается сама после каждого «+1 ГОТОВО»")}>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={pill(cfg.auto)} onClick={() => onChange({ auto: true })}>
              {t('✅ Включена')}
            </button>
            <button style={pill(!cfg.auto)} onClick={() => onChange({ auto: false })}>
              {t('⛔ Выключена')}
            </button>
          </div>
        </Row>

        <Row title={t("Размер бирки")} hint={t("Должен совпадать с размером этикетки в принтере")}>
          <div style={{ display: 'flex', gap: 10 }}>
            {[['mini', t('Мини · 30×20 мм')], ['small', t('Малая · 40×25 мм')],
              ['big', t('Большая · 70×50 мм')]].map(([v, label]) => (
              <button key={v} style={pill((cfg.size || 'small') === v)}
                onClick={() => onChange({ size: v })}>
                {label}
              </button>
            ))}
          </div>
        </Row>

        <Row title={t("Количество копий")} hint={t("Сколько бирок печатать на каждый лист")}>
          <div style={{ display: 'flex', gap: 10 }}>
            {[1, 2, 3].map(n => (
              <button key={n} style={pill(Number(cfg.copies) === n)}
                onClick={() => onChange({ copies: n })}>
                {n} {t('шт')}
              </button>
            ))}
          </div>
        </Row>

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button onClick={runTest} disabled={testing} style={{
            flex: 1, height: 52, borderRadius: 14, border: '2px solid var(--border)',
            background: 'transparent', color: 'var(--muted)', fontSize: 15,
            fontWeight: 700, cursor: testing ? 'wait' : 'pointer',
          }}>{testing ? '...' : t('🖨 Пробная бирка')}</button>
          <button onClick={onClose} style={{
            flex: 1, height: 52, borderRadius: 14, border: 'none',
            background: '#22c55e', color: '#fff', fontSize: 16, fontWeight: 800,
            cursor: 'pointer',
          }}>{t('Готово')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Список заданий пресса ─────────────────────────────────────────────────────
// Толщина спрятана в хвосте названия: «…2750*1830*16 мм» и «…*22 мм» с двух
// шагов неразличимы, а плита у пресса от этого зависит целиком. Выносим её
// отдельной плашкой и красим: цвет читается раньше цифры и не требует читать
// строку до конца.
const THICK_COLORS = {
  8:  '#0ea5e9', 10: '#14b8a6', 12: '#22c55e', 16: '#f59e0b',
  18: '#f97316', 22: '#ef4444', 25: '#a855f7', 28: '#6366f1',
};
const thickColor = (t) => THICK_COLORS[Math.round(Number(t))] || '#64748b';

// ─── Услуга: плиту привозит заказчик ──────────────────────────────────────
//
// У пресса это не пометка для памяти, а указание: плиту брать из привезённой
// стопки, а не со своего поддона. Ошибиться здесь дорого — чужая плита уедет
// заказчику, а его останется у нас, и обнаружится это на приёмке или позже.
// Поэтому метка крупная и стоит рядом с названием, а не в углу мелочью.
function ServiceBadge({ big = false }) {
  return (
    <span style={{
      background: '#8b5cf6', color: '#fff',
      borderRadius: 8, whiteSpace: 'nowrap',
      padding: big ? '4px 14px' : '2px 10px',
      fontSize: big ? 15 : 12.5, fontWeight: 800,
      letterSpacing: '.02em',
    }}>🧾 УСЛУГА · плита заказчика</span>
  );
}

function PlateBadge({ thickness, size = 'normal' }) {
  if (thickness == null) return null;
  const c = thickColor(thickness);
  const big = size === 'big';
  const txt = String(thickness).replace(/\.0+$/, '');
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'baseline', gap: 4,
      background: c + '22', border: '2px solid ' + c, color: c,
      borderRadius: big ? 12 : 9, padding: big ? '6px 16px' : '3px 11px',
      fontWeight: 800, fontSize: big ? 26 : 19, lineHeight: 1.1,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {txt}<span style={{ fontSize: big ? 14 : 11, fontWeight: 700 }}>мм</span>
    </span>
  );
}

// Пачка одинаковой работы: три заказа на один и тот же ЛДСП в одну смену —
// это для пресса одна работа на 37 листов, а не три по 10, 15 и 12. Плита та
// же, плёнка та же, матрицу менять не надо; заказы внутри остаются своими и
// закрываются по очереди сами (сервер переводит счёт, см. rollover_if_full).
function QueueCard({ item, pack, onAction, actionLabel, actionColor, disabled }) {
  const rows  = (pack && pack.length > 1) ? pack : null;
  const plan  = rows ? rows.reduce((n, x) => n + (x.quantity || 0), 0)
                     : item.quantity;
  const made  = rows ? rows.reduce((n, x) => n + (x.quantity_done || 0), 0)
                     : item.quantity_done;
  // План можно перевыполнить (14 из 5) — полоску при этом обрезаем по 100%,
  // иначе она вылезала за свою дорожку и закрашивала надпись рядом
  const progress = plan > 0 ? Math.min(100, Math.round((made / plan) * 100)) : 0;
  const overPlan = plan > 0 && made > plan;
  const isDone = item.status === 'done';

  return (
    <div style={{
      background: 'var(--card)', borderRadius: 14, padding: '18px 20px',
      border: item.status === 'in_progress' ? '2px solid #22c55e40' : '2px solid transparent',
      opacity: isDone ? 0.55 : 1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PlateBadge thickness={item.ref_thickness} />
            <div style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700 }}>{item.product_name}</div>
            {item.is_service && <ServiceBadge />}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 3 }}>
            {item.texture_name}
            <span style={{ color: 'var(--border)', margin: '0 8px' }}>·</span>
            {rows ? (
              <span style={{ color: '#f59e0b', fontWeight: 700 }}>
                {rows.length} заказа подряд
              </span>
            ) : (
              <>
                <span style={{ color: '#f59e0b' }}>#{item.order_number}</span>
                <span style={{ color: 'var(--border)', margin: '0 8px' }}>·</span>
                {item.client_name}
              </>
            )}
          </div>

          {/* Из чего сложились 37 листов. Прятать это нельзя: оператор должен
              видеть, чья работа идёт сейчас и чья пойдёт следом — по ней он
              отвечает на звонок «а мой заказ когда». */}
          {rows && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {rows.map((r, i) => (
                <div key={r.id} style={{
                  display: 'flex', gap: 10, alignItems: 'center',
                  fontSize: 14, color: i === 0 ? 'var(--text)' : 'var(--muted)',
                }}>
                  <span style={{ color: '#f59e0b', fontWeight: 700 }}>#{r.order_number}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                                 whiteSpace: 'nowrap' }}>{r.client_name}</span>
                  <b>{r.quantity_done > 0 ? `${r.quantity_done} / ` : ''}{r.quantity} шт</b>
                </div>
              ))}
            </div>
          )}

          {/* Условие заказа: «чфмк» — из какой плиты делать, «Упаковка 2х» —
              как паковать. Это называет клиент, менеджер записывает при
              заказе. Раньше доезжало только на бумаге, а оператор работает по
              экрану: указание он видел, если бумага лежала рядом и он о ней
              вспомнил. */}
          {(item.plate_size || item.note) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {item.plate_size && (
                <span style={{
                  background: '#f59e0b1f', border: '1px solid #f59e0b55',
                  color: '#f59e0b', borderRadius: 8, padding: '4px 12px',
                  fontSize: 15, fontWeight: 800,
                }}>{item.plate_size}</span>
              )}
              {item.note && (
                <span style={{
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  color: 'var(--text)', borderRadius: 8, padding: '4px 12px',
                  fontSize: 15, fontWeight: 700,
                }}>{item.note}</span>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <div style={{
              width: 160, flexShrink: 0, overflow: 'hidden',
              background: 'var(--bg)', borderRadius: 4, height: 6,
            }}>
              <div style={{
                width: `${progress}%`, height: 6, borderRadius: 4,
                background: isDone ? 'var(--border)'
                          : overPlan ? '#f59e0b' : 'var(--accent)'
              }} />
            </div>
            <span style={{ color: 'var(--muted)', fontSize: 14, whiteSpace: 'nowrap' }}>
              <b style={{ color: overPlan ? '#f59e0b' : 'var(--text)' }}>{made}</b>
              {' / '}{plan} {t('шт')}
              {rows && <span style={{ marginLeft: 6, color: 'var(--dim)' }}>всего по пачке</span>}
            </span>
          </div>
        </div>

        {onAction && !isDone && (
          <button onClick={onAction} disabled={disabled} style={{
            background: disabled ? 'var(--card)' : actionColor,
            border: disabled ? '1px solid var(--border)' : 'none',
            borderRadius: 12, color: disabled ? 'var(--mid)' : '#fff',
            padding: '16px 28px', fontSize: 17, fontWeight: 700,
            cursor: disabled ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap', marginLeft: 16
          }}>{actionLabel}</button>
        )}
      </div>
    </div>
  );
}

// ── Правка выпуска: цифру чинит тот, кто её создал ───────────────────────────
//
// Кладовщик пересчитал стопку и получил меньше, чем в системе: оператор
// пережал +1 или лист разбили по дороге. Уменьшать приёмку на складе нельзя —
// расход ДСП и плёнки считается по ВЫПУСКУ, а не по тому, сколько доехало.
// Поправишь только приёмку — сырьё останется списанным за листы, которых не
// было, и остаток разъедется молча.
//
// Поэтому склад говорит прессу, пресс правит здесь, склад принимает заново.
// Расход по рецепту едет следом сам.
function FixQuantityModal({ job, onClose, onDone }) {
  const [qty, setQty]       = useState(job.quantity_done);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const REASONS = ['Пережал +1', 'Лист разбили', 'Пересчитали стопку'];
  const delta = qty - job.quantity_done;

  const save = async () => {
    if (delta === 0) { toast.error(t('Число не изменилось')); return; }
    setSaving(true);
    try {
      await productionAPI.correctQuantity(job.id, { quantity: qty, reason });
      toast.success(`✅ ${job.quantity_done} → ${qty}`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setSaving(false); }
  };

  const step = (n) => setQty(v => Math.max(0, v + n));

  const bigBtn = (label, n, color) => (
    <button onClick={() => step(n)} className="touch-btn" style={{
      background: 'var(--card)', border: `2px solid ${color}`, borderRadius: 14,
      color, fontSize: 26, fontWeight: 800, height: 64, cursor: 'pointer',
      fontFamily: 'inherit',
    }}>{label}</button>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 18,
        width: '100%', maxWidth: 560, padding: 24,
      }}>
        <div style={{ color: 'var(--text)', fontSize: 20, fontWeight: 800 }}>
          {job.product_name}
        </div>
        <div style={{ color: 'var(--mid)', fontSize: 14, marginTop: 4 }}>
          {t('Склад ещё не забрал')} · {t('нажатий')}: {job.presses}
        </div>

        <div style={{
          marginTop: 18, background: 'var(--card)', borderRadius: 14,
          padding: '18px 20px', textAlign: 'center',
        }}>
          <div style={{ color: 'var(--mid)', fontSize: 14 }}>
            {t('Было')}: <b style={{ color: 'var(--text)' }}>{job.quantity_done}</b> {t('шт')}
          </div>
          <div style={{
            color: delta === 0 ? 'var(--text)' : (delta < 0 ? '#f59e0b' : '#22c55e'),
            fontSize: 72, fontWeight: 900, lineHeight: 1.1,
          }}>{qty}</div>
          {delta !== 0 && (
            <div style={{ color: delta < 0 ? '#f59e0b' : '#22c55e', fontSize: 16, fontWeight: 700 }}>
              {delta > 0 ? '+' : ''}{delta} {t('шт')}
            </div>
          )}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 14,
          }}>
            {bigBtn('−5', -5, '#f59e0b')}
            {bigBtn('−1', -1, '#f59e0b')}
            {bigBtn('+1', +1, '#22c55e')}
            {bigBtn('+5', +5, '#22c55e')}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {REASONS.map(r => (
            <button key={r} onClick={() => setReason(reason === r ? '' : r)}
              className="touch-btn" style={{
                background: reason === r ? 'var(--accent)' : 'var(--card)',
                border: '1px solid var(--border)', borderRadius: 20,
                color: reason === r ? '#fff' : 'var(--mid)', padding: '9px 16px',
                fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
              }}>{r}</button>
          ))}
        </div>

        <div style={{ color: 'var(--mid)', fontSize: 13, marginTop: 14, lineHeight: 1.5 }}>
          {t('Расход ДСП и плёнки пересчитается сам: сырьё списывается по выпуску.')}
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 18 }}>
          <button onClick={onClose} className="touch-btn" style={{
            flex: 1, background: 'var(--card)', border: '1px solid var(--border)',
            borderRadius: 12, color: 'var(--mid)', height: 58, fontSize: 16,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>{t('Отмена')}</button>
          <button onClick={save} disabled={saving || delta === 0} className="touch-btn" style={{
            flex: 2, background: (saving || delta === 0) ? 'var(--card)' : '#22c55e',
            border: 'none', borderRadius: 12,
            color: (saving || delta === 0) ? 'var(--dim)' : '#fff', height: 58,
            fontSize: 18, fontWeight: 800,
            cursor: (saving || delta === 0) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{saving ? '…' : t('Сохранить')}</button>
        </div>
      </div>
    </div>
  );
}

// Собрать задания в пачки одинаковой работы. Ключ считает СЕРВЕР (group_key):
// так у экрана пресса и у моноблока пачки получаются одни и те же, и спорить
// им не о чем. Пачка встаёт на место своего первого задания — порядок, который
// выстроил технолог, не меняется.
function packs(items) {
  const out = [];
  const byKey = new Map();
  (items || []).forEach(it => {
    const key = it.group_key || ('one-' + it.id);
    if (byKey.has(key)) { byKey.get(key).push(it); return; }
    const pack = [it];
    byKey.set(key, pack);
    out.push(pack);
  });
  // Внутри пачки — тот же порядок, в котором сервер переводит счёт: раньше
  // срок — раньше закроется (без срока — в конце), потом очередь технолога.
  // Иначе «следом» на экране называло бы не тот заказ, в который лягут листы.
  out.forEach(pack => pack.sort(byWorkOrder));
  return out;
}

// Порядок работы внутри пачки — такой же, как у сервера (grouping.group_items)
function byWorkOrder(a, b) {
  const due = x => x.deadline || '9999-12-31';
  return due(a).localeCompare(due(b)) || (a.priority - b.priority) || (a.id - b.id);
}

function PressQueueScreen({ press, worker, onSelectJob, onChangePress }) {
  const [queue, setQueue]   = useState([]);
  const [loading, setLoading] = useState(true);

  // «Загрузка…» — только при первом открытии. Фоновое обновление раз в
  // двадцать секунд гасило список и возвращало обратно: экран моргал, а
  // вместе с ним перерисовывалось окно параметров, в котором в этот момент
  // набирали температуру.
  const firstLoad = useRef(true);
  const loadQueue = useCallback(() => {
    if (firstLoad.current) setLoading(true);
    productionAPI.getQueue({ press_id: press.id })
      .then(r => setQueue(r.data))
      .catch(() => { if (firstLoad.current) toast.error(t("Ошибка загрузки")); })
      .finally(() => { firstLoad.current = false; setLoading(false); });
  }, [press.id]);

  useEffect(() => { loadQueue(); }, [loadQueue]);
  useEffect(() => {
    const t = setInterval(loadQueue, 20000);
    return () => clearInterval(t);
  }, [loadQueue]);

  const [paramsModal, setParamsModal] = useState(null); // { item, mode }
  // Чтобы confirmParams не пересоздавался ради одного чтения
  const paramsRef = useRef(null);
  useEffect(() => { paramsRef.current = paramsModal; }, [paramsModal]);
  const [starting,     setStarting]   = useState(false);

  // Смены, которые склад ещё не забрал. Кладовщик пересчитал стопку, цифра не
  // сошлась — он идёт к прессу, и оператор правит здесь. На складе править
  // нельзя: расход ДСП и плёнки считается по выпуску, а не по приёмке.
  const [awaiting, setAwaiting] = useState([]);
  const [fixJob,   setFixJob]   = useState(null);

  const loadAwaiting = useCallback(() => {
    productionAPI.awaitingJobs(press.id)
      .then(r => setAwaiting(r.data.results || []))
      .catch(() => {});
  }, [press.id]);

  useEffect(() => { loadAwaiting(); }, [loadAwaiting]);

  const openStartModal  = (item) => setParamsModal({ item, mode: 'start' });
  // 10-иш: тугатмасдан чиққан оператор қайта кирганда параметрлар қайта сўралади
  const openResumeModal = (item) => setParamsModal({ item, mode: 'resume' });

  // Ссылки постоянные — иначе React.memo на окне ничего не даёт: новая
  // функция в пропсах считается изменением, и окно перерисовывается на
  // каждый фоновый ответ.
  // Пока окно параметров открыто, программа не перезагружается: там набраны
  // температуры и партия, и обновление стёрло бы их вместе со сменой.
  useEffect(() => { setBusy(!!paramsModal); return () => setBusy(false); },
            [paramsModal]);

  const closeParams = useCallback(() => setParamsModal(null), []);
  const confirmParams = useCallback(async (payload) => {
    const { item, mode } = paramsRef.current || {};
    if (!item) return;
    setStarting(true);
    try {
      if (mode === 'start') {
        // Проверок у старта две — плита и смена, — и сервер называет их по
        // одной: сначала первую, потом вторую. Раньше подтверждение второй
        // уходило в общий обработчик ошибок и вопрос просто не появлялся, а
        // задание не начиналось. Поэтому здесь цикл: сколько раз спросят,
        // столько раз и ответим.
        const okd = {};
        for (;;) {
          try {
            await productionAPI.startJob(item.id, { ...payload, ...okd });
            break;
          } catch (e) {
            const d = e.response?.data || {};
            if (e.response?.status !== 409) throw e;

            if (d.plate_warning && !okd.confirm_plate) {
              // Плиту меняют руками у станка, и спрашиваем до первого листа:
              // после него испорчена уже вся стопка.
              const ok = await confirmToast(
                `Прошлая позиция — ${d.was} мм, эта — ${d.now} мм.
Плиту на прессе поменяли?`,
                { title: 'Другая плита', confirmText: `Да, плита ${d.now} мм`,
                  danger: true, big: true });
              if (!ok) { setStarting(false); return; }
              okd.confirm_plate = true;
              continue;
            }
            if (d.shift_warning && !okd.confirm_shift) {
              const ok = await confirmToast(
                `Это работа смены «${d.planned_display}», а сейчас «${d.now_display}».
Уточните у технолога. Всё равно начать?`,
                { title: 'Другая смена', confirmText: 'Всё равно начать',
                  danger: true, big: true });
              if (!ok) { setStarting(false); return; }
              okd.confirm_shift = true;
              continue;
            }
            throw e;
          }
        }
        toast.success(t("Задание начато!"));
      } else {
        await productionAPI.setJobParams(item.id, payload);
        toast.success(t("Параметры обновлены"));
      }
      setParamsModal(null);
      onSelectJob({ ...item, status: 'in_progress' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setStarting(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSelectJob]);

  // ВСЕ начатые работы, а не первая. Оператор мог начать одну, выйти
  // и начать другую — при find вторая пропадала с экрана совсем: в
  // «в очереди» она не попадает (статус уже in_progress), и найти её
  // было негде. Именно так и терялась работа.
  // Какая смена идёт прямо сейчас. Границы те же, что у технолога: ночь с
  // 17 до 8. Точное значение живёт на сервере — здесь оно нужно только для
  // порядка групп, и ошибка на час порядок не ломает.
  const nowHour = new Date().getHours();
  const nowShift = (nowHour >= 17 || nowHour < 8) ? 'night' : 'day';

  const activeJobs  = queue.filter(q => q.status === 'in_progress');
  const waitingJobs = queue.filter(q => q.status === 'waiting');

  // Очередь по сменам: своя сверху. Порядок внутри группы не трогаем — его
  // выстроил технолог стрелками, и переставлять его здесь нельзя.
  const shiftGroups = ['day', 'night']
    .sort((a, b) => (a === nowShift ? -1 : b === nowShift ? 1 : 0))
    .map(key => {
      const items = waitingJobs.filter(x => (x.shift || 'day') === key);
      return { key, mine: key === nowShift, items, list: packs(items) };
    })
    .filter(g => g.items.length > 0);
  const doneJobs    = queue.filter(q => q.status === 'done');

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)' }}>

      {/* Parametrlar modali (boshlash / davom ettirish) */}
      {paramsModal && (
        <PressParamsModal
          item={paramsModal.item}
          mode={paramsModal.mode}
          press={press}
          saving={starting}
          onConfirm={confirmParams}
          onCancel={closeParams}
        />
      )}

      <div style={{
        background: 'var(--card)', borderBottom: '1px solid var(--border)',
        padding: '0 24px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 18 }}>🏭</span>
          <span style={{ fontWeight: 700, fontSize: 17 }}>{press.name}</span>
          {/* Имя на виду: смена должна видеть, на кого пишется выпуск —
              иначе чужой ПИН заметят только в отчёте за месяц */}
          {worker && (
            <span style={{
              background: 'var(--accent-weak)', color: 'var(--accent)',
              borderRadius: 20, padding: '4px 12px', fontSize: 13, fontWeight: 700,
            }}>👤 {worker.name}</span>
          )}
          {press.current_texture_name && (
            <span style={{
              background: 'var(--bg)', borderRadius: 6,
              padding: '3px 10px', color: 'var(--muted)', fontSize: 13
            }}>{press.current_texture_name}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Матрицу меняют как раз здесь, между заданиями */}
          <StopPanel press={press} />
          <FullscreenBtn />
          <ThemeBtn />
          <LangSwitch compact />
          <Clock />
          <button onClick={loadQueue} style={{
            background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--muted)', padding: '7px 12px', cursor: 'pointer'
          }}>🔄</button>
          {/* Под учёткой пресса менять нечего: этот монитор стоит у этого
              станка и никуда не переезжает */}
          {onChangePress && (
            <button onClick={onChangePress} style={{
              background: 'none', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--dim)', padding: '7px 14px',
              cursor: 'pointer', fontSize: 13
            }}>{t('Сменить пресс')}</button>
          )}
        </div>
      </div>

      {fixJob && (
        <FixQuantityModal
          job={fixJob}
          onClose={() => setFixJob(null)}
          onDone={() => { setFixJob(null); loadAwaiting(); loadQueue(); }}
        />
      )}

      <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
        {/* Ждут склад: пока не забрали — цифру ещё можно поправить */}
        {awaiting.length > 0 && (
          <div style={{ marginBottom: 22 }}>
            <div style={{
              color: 'var(--mid)', fontSize: 13, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10
            }}>📦 {t('ЖДУТ СКЛАД')} ({awaiting.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {awaiting.map(j => (
                <div key={j.id} style={{
                  background: 'var(--card)', borderRadius: 14, padding: '14px 18px',
                  display: 'flex', alignItems: 'center', gap: 14,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      color: 'var(--text)', fontSize: 16, fontWeight: 700,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{j.product_name}</div>
                    <div style={{ color: 'var(--mid)', fontSize: 13, marginTop: 3 }}>
                      {t('выпуск')}: <b style={{ color: 'var(--text)' }}>{j.quantity_done}</b> {t('шт')}
                      {j.presses !== j.quantity_done && <> · {t('нажатий')}: {j.presses}</>}
                    </div>
                  </div>
                  {j.can_fix ? (
                    <button onClick={() => setFixJob(j)} className="touch-btn" style={{
                      background: 'var(--bg)', border: '2px solid #f59e0b', borderRadius: 12,
                      color: '#f59e0b', padding: '12px 20px', fontSize: 15, fontWeight: 700,
                      cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
                    }}>{t('Исправить')}</button>
                  ) : (
                    <span style={{ color: 'var(--dim)', fontSize: 13, flexShrink: 0 }}>
                      {t('склад забрал')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <p style={{ color: 'var(--mid)', fontSize: 18, textAlign: 'center', paddingTop: 60 }}>{t('Загрузка...')}</p>
        ) : queue.length === 0 ? (
          <div style={{ textAlign: 'center', paddingTop: 80 }}>
            <div style={{ fontSize: 64, marginBottom: 20 }}>✅</div>
            <p style={{ color: 'var(--mid)', fontSize: 20, fontWeight: 600 }}>{t('Нет заданий')}</p>
            <p style={{ color: 'var(--border)', fontSize: 15, marginTop: 8 }}>
              Ожидайте назначения от технолога
            </p>
          </div>
        ) : (
          <>
            {activeJobs.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{
                  color: '#22c55e', fontSize: 13, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10
                }}>{t('▶ В РАБОТЕ')} ({activeJobs.length})</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {activeJobs.map(item => (
                    <QueueCard
                      key={item.id}
                      item={item}
                      actionLabel="Продолжить →"
                      actionColor="#22c55e"
                      onAction={() => openResumeModal(item)}
                    />
                  ))}
                </div>
              </div>
            )}

            {waitingJobs.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{
                  color: 'var(--muted)', fontSize: 13, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10
                }}>⏳ В ОЧЕРЕДИ ({waitingJobs.length})</div>

                {/* Своя смена сверху, чужая ниже. Сплошным списком оператор
                    брал следующую строку не глядя и начинал ночную работу в
                    обед; ночью её потом искали. Чужую не прячем — бывает, что
                    берут и её, но теперь это видно. */}
                {shiftGroups.map(g => (
                  <div key={g.key} style={{ marginBottom: 14 }}>
                    {shiftGroups.length > 1 && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 14px', borderRadius: 10, marginBottom: 10,
                        background: g.key === 'night' ? '#6366f11f' : '#22c55e1f',
                        border: '1px solid ' + (g.key === 'night' ? '#6366f155' : '#22c55e55'),
                        color: g.key === 'night' ? '#818cf8' : '#22c55e',
                        fontSize: 15, fontWeight: 800,
                      }}>
                        {g.key === 'night' ? '🌙 НОЧНАЯ СМЕНА' : '☀️ ДНЕВНАЯ СМЕНА'}
                        <span style={{ opacity: .8, fontWeight: 600 }}>
                          {g.items.length}
                        </span>
                        {!g.mine && (
                          <span style={{
                            marginLeft: 'auto', fontSize: 12.5, fontWeight: 600,
                            opacity: .9,
                          }}>это работа не вашей смены</span>
                        )}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {g.list.map((pack, i) => {
                        const item = pack[0];
                        // Матрица сменилась — предупреждаем: дальше переналадка
                        const prev = i > 0 ? g.list[i - 1][0] : null;
                        const changeMatrix = prev && prev.texture_name !== item.texture_name;
                        // Ошибка рождается ровно здесь: прессовали 16-ю, следом
                        // в очереди 22-я, и плиту никто не поменял.
                        const changePlate = prev
                          && prev.ref_thickness != null && item.ref_thickness != null
                          && Number(prev.ref_thickness) !== Number(item.ref_thickness);
                        return (
                          <React.Fragment key={item.id}>
                            {changePlate && (
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '12px 16px', borderRadius: 12,
                                background: thickColor(item.ref_thickness) + '1f',
                                border: '2px solid ' + thickColor(item.ref_thickness),
                                color: thickColor(item.ref_thickness),
                                fontSize: 17, fontWeight: 800,
                              }}>
                                ⚠️ ДРУГАЯ ПЛИТА
                                <PlateBadge thickness={item.ref_thickness} />
                                <span style={{ fontSize: 14, fontWeight: 600, opacity: .85 }}>
                                  было {String(prev.ref_thickness).replace(/\.0+$/, '')} мм
                                </span>
                              </div>
                            )}
                            {changeMatrix && (
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '9px 14px', borderRadius: 10,
                                background: '#f59e0b18', border: '1px solid #f59e0b55',
                                color: '#f59e0b', fontSize: 15, fontWeight: 700,
                              }}>
                                🔧 замена матрицы &raquo; {item.texture_name}
                              </div>
                            )}
                            <QueueCard
                              item={item} pack={pack}
                              actionLabel="▶ Начать" actionColor="var(--accent)"
                              onAction={() => openStartModal(item)}
                            />
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {doneJobs.length > 0 && (
              <div>
                <div style={{
                  color: 'var(--mid)', fontSize: 13, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10
                }}>✅ ВЫПОЛНЕНО ({doneJobs.length})</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {doneJobs.map(item => <QueueCard key={item.id} item={item} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Кто за прессом: вход по ПИНу ─────────────────────────────────────────────
//
// На моноблоке вход общий: логин с паролем на каждую пересменку никто набирать
// не станет, и весь выпуск смены записывался на одну учётку operator1. Кто
// именно работал — по журналу было не восстановить.
//
// Пресс выбран — спрашиваем ПИН. Человек держится до смены пресса или выхода:
// внутри смены его не переспрашивают, иначе цифры начнут набирать не глядя.
//
// Общей подсказки на экране больше нет. Пока она висела, её набирали вместо
// своего ПИНа — и вся смена писалась на одну учётку, то есть авторства не
// было ни у кого. ПИНы розданы, подсказка убрана.
function PinScreen({ press, onOk, onBack }) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (value) => {
    const v = String(value ?? pin);
    if (v.length < 4) { toast.error(t('ПИН — от 4 до 6 цифр')); return; }
    setBusy(true);
    try {
      const { data } = await productionAPI.pressPin(v);
      toast.success(`👋 ${data.name}`);
      onOk(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'ПИН не найден');
      setPin('');
    } finally { setBusy(false); }
  };

  const push = (d) => {
    if (busy || pin.length >= 6) return;
    const next = pin + d;
    setPin(next);
    // 6 цифр — дальше набирать нечего, проверяем сразу
    if (next.length === 6) submit(next);
  };

  const KEY = {
    height: 68, borderRadius: 14, border: '1px solid var(--border)',
    background: 'var(--card)', color: 'var(--text)', fontSize: 28, fontWeight: 800,
    cursor: 'pointer', fontFamily: 'inherit',
  };

  // Монитор у пресса широкий, но невысокий: всё в одну колонку не помещалось
  // и уезжало под вертикальный скролл. Текст с подсказкой уходит влево,
  // клавиатура — вправо, экран перестаёт прокручиваться.
  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div className="pin-wrap">
        <div className="pin-info">
          <div style={{ color: 'var(--dim)', fontSize: 14 }}>{press.name}</div>
          <div style={{ fontSize: 28, fontWeight: 800, margin: '2px 0 4px' }}>Кто работает?</div>
          <div style={{ color: 'var(--dim)', fontSize: 13 }}>
            Введите свой ПИН — выпуск запишется на вас
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 16, height: 20 }}>
            {[0, 1, 2, 3, 4, 5].map(i => (
              <span key={i} style={{
                width: 16, height: 16, borderRadius: '50%',
                background: i < pin.length ? 'var(--accent)' : 'transparent',
                border: '2px solid ' + (i < pin.length ? 'var(--accent)' : 'var(--border)'),
              }} />
            ))}
          </div>

          <button onClick={onBack} className="touch-btn" style={{
            marginTop: 18, background: 'none', border: '1px solid var(--border)',
            borderRadius: 12, color: 'var(--muted)', padding: '11px 22px',
            fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', alignSelf: 'flex-start',
          }}>← Другой пресс</button>
        </div>

        <div className="pin-pad">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
            <button key={d} className="touch-btn" style={KEY} onClick={() => push(String(d))}>{d}</button>
          ))}
          <button className="touch-btn" style={{ ...KEY, fontSize: 22, color: 'var(--muted)' }}
            onClick={() => setPin(p => p.slice(0, -1))}>⌫</button>
          <button className="touch-btn" style={KEY} onClick={() => push('0')}>0</button>
          <button className="touch-btn" disabled={busy || pin.length < 4}
            style={{
              ...KEY,
              background: pin.length >= 4 ? '#22c55e' : 'var(--card)',
              color: pin.length >= 4 ? '#fff' : 'var(--dim)',
              border: pin.length >= 4 ? 'none' : '1px solid var(--border)',
              fontSize: 26,
            }}
            onClick={() => submit()}>✓</button>
        </div>
      </div>
    </div>
  );
}

// ── Главный компонент ─────────────────────────────────────────────────────────
export default function OperatorTerminal() {
  const [selectedPress, setSelectedPress] = useState(null);
  const [worker, setWorker]               = useState(null);
  const [activeJob, setActiveJob]         = useState(null);
  // Учётка привязана к прессу — тогда выбирать нечего и менять тоже.
  // Монитор у станка стоит на месте: место знает учётка, а не человек.
  const [boundPress, setBoundPress]       = useState(null);

  useEffect(() => {
    productionAPI.getMe()
      .then(r => {
        if (r.data && r.data.press) {
          const p = { id: r.data.press, name: r.data.press_name || 'Пресс' };
          setBoundPress(p);
          setSelectedPress(prev => prev || p);
        }
      })
      .catch(() => {});
  }, []);

  // Пресс освобождается тогда, когда оператор ушёл: нажал «Выйти»/«Сменить
  // пресс» или закрыл вкладку. По времени пресс НЕ отбирают — пока моноблок
  // работает, он каждые полминуты отмечается, и смену никто не выгонит.
  //
  // Срок протухания (15 минут) нужен ровно на один случай: терминал умер —
  // выключили питание, завис, браузер упал — и сказать «я ушёл» уже некому.
  // Короткий срок здесь опасен: при рывке сети пресс отобрали бы у живой смены.
  useEffect(() => {
    if (!selectedPress) return undefined;
    const id = selectedPress.id;
    const ping = () => productionAPI.claimPress(id, clientId).catch(err => {
      if (err.response?.status === 409) {
        toast.error(err.response.data?.error || t('Пресс занят другим моноблоком'));
        setActiveJob(null);
        setSelectedPress(null);
      }
    });
    const timer = setInterval(ping, 30000);
    // Закрыли вкладку — отпускаем сразу, не дожидаясь протухания
    const bye = () => productionAPI.releasePressBeacon(id, clientId);
    window.addEventListener('pagehide', bye);
    window.addEventListener('beforeunload', bye);
    return () => {
      clearInterval(timer);
      window.removeEventListener('pagehide', bye);
      window.removeEventListener('beforeunload', bye);
    };
  }, [selectedPress]);

  const leavePress = () => {
    if (selectedPress) productionAPI.releasePress(selectedPress.id, clientId).catch(() => {});
    setActiveJob(null);
    setSelectedPress(null);
    // Ушли от пресса — авторство сбрасывается: следующая смена вводит свой ПИН
    setWorker(null);
    setPressWorker(null);
  };

  if (!selectedPress) {
    // Учётка пресса ещё грузится — списком прессов её не перебиваем
    if (boundPress) return null;
    return <SelectPressScreen onSelect={setSelectedPress} />;
  }

  // Пресс выбран, но не сказано кто работает — дальше не пускаем: иначе
  // выпуск снова запишется на общую учётку и авторство потеряется.
  if (!worker) return (
    <PinScreen
      press={selectedPress}
      onOk={(w) => { setWorker(w); setPressWorker(w.id); }}
      onBack={leavePress}
    />
  );

  if (activeJob) return (
    <ActiveJobScreen
      // key: у следующего заказа пачки свой план и свой счёт — экран
      // собирается заново, а не донашивает цифры прежнего
      key={activeJob.id}
      item={activeJob}
      press={selectedPress}
      worker={worker}
      onBack={() => setActiveJob(null)}
      onCompleted={() => setActiveJob(null)}
      onSwitched={async (qid) => {
        try {
          const r = await productionAPI.getQueue({ press_id: selectedPress.id });
          const next = (r.data || []).find(x => x.id === qid);
          setActiveJob(next || null);
        } catch (e) {
          setActiveJob(null);
        }
      }}
    />
  );

  return (
    <PressQueueScreen
      press={selectedPress}
      worker={worker}
      onSelectJob={setActiveJob}
      onChangePress={boundPress ? null : leavePress}
    />
  );
}